import React, { useState, useEffect, useCallback, useMemo, useRef, startTransition } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Autocomplete,
  InputAdornment,
  Stack,
  IconButton,
  TablePagination,
  Checkbox,
  Chip,
  TableSortLabel,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  ListSubheader,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { AutocompleteRenderGroupParams, AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import {
  AccountBalance as AccountBalanceIcon,
  Payments as PaymentsIcon,
  CreditCard as CreditCardIcon,
  Receipt as ReceiptIcon,
  MoreHoriz as MoreHorizIcon,
  Search as SearchIcon,
  Check as CheckIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  FilterAlt as FilterAltIcon,
  ExpandMore as ExpandMoreIcon,
  Notes as NotesIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useLocation, useNavigate } from 'react-router-dom';
import { esChequeDisponibleEnCartera } from '../../utils/chequeTerceroEstado';
import { formatNroCheque, formatNroChequeVista } from '../../utils/nroCheque';
import { MCP_TIPOS } from '../../services/mcpService';
import tesoreriaService, {
  GrupoCajaChica,
  MedioTransferencia,
  ChequeCartera,
  ChequesCarteraQueryParams,
  CuponCartera,
  TarjetasCarteraQueryParams,
} from '../../services/tesoreriaService';
import tesoreriaCajaService from '../../services/tesoreriaCajaService';
import grupoMcpService, { GrupoMcp } from '../../services/grupoMcpService';
import mcpService from '../../services/mcpService';
import { formatCurrency } from '../../utils/formatters';
import api from '../../services/api';
import PDFViewer from '../../components/shared/PDFViewer';
import ExportProgressDialog, { ExportProgressState } from '../../components/shared/ExportProgressDialog';
import SearchableSelect, { searchableSelectAutocompleteProps } from '../../components/SearchableSelect';
import FiltroSucursalVentaSelect from '../../components/shared/FiltroSucursalVentaSelect';
import { resolveNroSucFiltro, useSucursalesVentaFiltro } from '../../hooks/useSucursalesVentaFiltro';
import { useSucursalAltaFormulario } from '../../hooks/useSucursalAltaFormulario';
import { useSucursalesUsuarioContext } from '../../contexts/SucursalesUsuarioContext';
import {
  loadTesoreriaFiltros,
  storedNumber,
  storedString,
  TESORERIA_FILTROS_KEYS,
  usePersistTesoreriaFiltros,
} from '../../utils/tesoreriaFiltrosStorage';
import { getAccessToken } from '../../utils/sessionAuthStore';

interface TransferenciasListadoFiltrosStored {
  searchTerm?: string;
  fechaDesde?: string;
  fechaHasta?: string;
  filtroNroSuc?: number | null;
  rowsPerPage?: number;
}

const ESTADO_CHEQUE_OPTIONS = [
  { value: 'en_cartera', label: 'En cartera' },
  { value: 'entregado', label: 'Entregado' },
  { value: 'conciliado', label: 'Conciliado' },
] as const;

interface Sucursal {
  nroSuc: number;
  nombreSucursal: string;
}

interface SucursalBancoRow {
  nroBanco: number;
  nroSucursal: number;
  sucursal: string;
}

export interface TransferenciaItem {
  id?: number;
  nroFormulario?: number;
  nroTipoFormulario?: string;
  nroSuc?: number;
  fechaEmision?: string;
  codigoCaja?: number;
  usuario?: string;
  nombre?: string;
  descripcion?: string;
  importeTotal?: number;
  letra?: string;
  nroComprobante?: number;
  codigoCajaOrigen?: number;
  codigoCajaDestino?: number;
  mcpOrigen?: string;
  mcpDestino?: string;
  origen?: string;
  destino?: string;
  detalle?: string;
  monto?: number;
  importe?: number;
  medios?: Array<{ mcp: string; tipoPago: string; detalle: string; importe: number; nroCuenta: number; ingresoEgreso: string }>;
  [key: string]: any;
}

const TIPO_EFECTIVO = 'E';
const TIPO_CHEQUE = 'C';
const TIPO_CHEQUE_PROPIO = 'P';
const TIPO_TRANSFERENCIA = 'TB';
const TIPO_TARJETA = 'T';
const TIPO_OTROS = 'O';
const TIPO_RENDICION = 'R'; // Se trata igual que "Otros"

type CategoriaMedioKey = 'efectivo' | 'cheque' | 'transferencia' | 'tarjeta' | 'otros';

const ORDEN_CATEGORIA_MEDIO: CategoriaMedioKey[] = [
  'efectivo',
  'cheque',
  'transferencia',
  'tarjeta',
  'otros',
];

const ETIQUETA_CATEGORIA_MEDIO: Record<CategoriaMedioKey, string> = {
  efectivo: 'Efectivo',
  cheque: 'Cheques',
  transferencia: 'Transferencia bancaria',
  tarjeta: 'Tarjeta',
  otros: 'Otros',
};

const ICONO_POR_CATEGORIA_MEDIO: Record<CategoriaMedioKey, typeof AccountBalanceIcon> = {
  efectivo: PaymentsIcon,
  cheque: ReceiptIcon,
  transferencia: AccountBalanceIcon,
  tarjeta: CreditCardIcon,
  otros: MoreHorizIcon,
};

function getCategoriaMedioKey(tipo?: string | null): CategoriaMedioKey {
  const t = (tipo ?? '').toString().trim().toUpperCase();
  if (t === TIPO_EFECTIVO) return 'efectivo';
  if (t === TIPO_CHEQUE) return 'cheque';
  if (t === TIPO_TRANSFERENCIA) return 'transferencia';
  if (t === TIPO_TARJETA) return 'tarjeta';
  return 'otros';
}

function getEtiquetaCategoriaMedio(tipo?: string | null): string {
  return ETIQUETA_CATEGORIA_MEDIO[getCategoriaMedioKey(tipo)];
}

function getEtiquetaTipoMedio(tipo?: string | null): string {
  const t = (tipo ?? '').trim().toUpperCase();
  if (!t) return '';
  const found = MCP_TIPOS.find((x) => x.value === t);
  if (found) return found.label.replace(/^[^—]+—\s*/, '').trim();
  return getEtiquetaCategoriaMedio(tipo);
}

function getMedioAutocompleteLabel(medio: MedioTransferencia | null): string {
  if (!medio) return '';
  const titulo = (medio.nombre || medio.descripcion || medio.mcp || '').toString().trim();
  const mcp = (medio.mcp ?? '').toString().trim();
  if (titulo && mcp && titulo !== mcp) return `${titulo} (${mcp})`;
  return titulo || mcp || 'Sin nombre';
}

function textoBusquedaMedio(opt: MedioTransferencia): string {
  return [
    opt.nombre,
    opt.descripcion,
    opt.mcp,
    opt.clase,
    opt.tipo,
    getEtiquetaTipoMedio(opt.tipo),
    getEtiquetaCategoriaMedio(opt.tipo),
    opt.grupo != null ? String(opt.grupo) : '',
    opt.nroSuc != null ? String(opt.nroSuc) : '',
  ]
    .map((x) => (x == null ? '' : String(x)))
    .join(' ')
    .toLowerCase();
}

function compararMediosPorCategoria(a: MedioTransferencia, b: MedioTransferencia): number {
  const ia = ORDEN_CATEGORIA_MEDIO.indexOf(getCategoriaMedioKey(a.tipo));
  const ib = ORDEN_CATEGORIA_MEDIO.indexOf(getCategoriaMedioKey(b.tipo));
  if (ia !== ib) return ia - ib;
  return getMedioAutocompleteLabel(a).localeCompare(getMedioAutocompleteLabel(b), 'es');
}

function renderGrupoMedioAutocomplete(params: AutocompleteRenderGroupParams) {
  return (
    <li key={params.key}>
      <ListSubheader
        sx={{
          bgcolor: '#f0f4f8',
          color: 'text.secondary',
          fontWeight: 600,
          fontSize: '0.75rem',
          lineHeight: '32px',
          textTransform: 'uppercase',
          letterSpacing: 0.5,
        }}
      >
        {params.group}
      </ListSubheader>
      <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
    </li>
  );
}

function getMedioTituloYCodigo(medio: MedioTransferencia | null): { titulo: string; mcp: string } {
  if (!medio) return { titulo: '', mcp: '' };
  const titulo = (medio.nombre || medio.descripcion || medio.mcp || '').toString().trim();
  const mcp = (medio.mcp ?? '').toString().trim();
  return { titulo, mcp };
}

function renderMedioAutocompleteInput(
  params: AutocompleteRenderInputParams,
  label: string,
  selected: MedioTransferencia | null,
  isOpen: boolean,
  placeholder = 'Buscar...'
) {
  const mostrarValorEstilizado = !!selected && !isOpen;
  const { titulo, mcp } = getMedioTituloYCodigo(selected);
  const tieneCodigo = Boolean(mcp && mcp !== titulo);
  const Icon = selected
    ? ICONO_POR_CATEGORIA_MEDIO[getCategoriaMedioKey(selected.tipo)]
    : null;

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <TextField
        {...params}
        label={label}
        placeholder={placeholder}
        inputProps={{
          ...params.inputProps,
          ...(mostrarValorEstilizado ? { readOnly: true } : {}),
        }}
        sx={
          mostrarValorEstilizado
            ? {
                '& .MuiInputBase-input': {
                  color: 'transparent',
                  caretColor: 'transparent',
                  WebkitTextFillColor: 'transparent',
                },
              }
            : undefined
        }
      />
      {mostrarValorEstilizado && Icon ? (
        <Stack
          direction="row"
          spacing={1.25}
          alignItems="center"
          sx={{
            position: 'absolute',
            left: 12,
            right: 40,
            top: 22,
            bottom: 9,
            pointerEvents: 'none',
            overflow: 'hidden',
            minWidth: 0,
          }}
        >
          <Icon sx={{ color: '#1976d2', fontSize: 22, flexShrink: 0 }} />
          <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {titulo}
            </Typography>
            {tieneCodigo ? (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{
                  lineHeight: 1.15,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {mcp}
              </Typography>
            ) : null}
          </Box>
        </Stack>
      ) : null}
    </Box>
  );
}

function renderOpcionMedioAutocomplete(
  props: React.HTMLAttributes<HTMLLIElement>,
  option: MedioTransferencia,
  keyPrefix: string
) {
  const categoria = getCategoriaMedioKey(option.tipo);
  const Icon = ICONO_POR_CATEGORIA_MEDIO[categoria];
  const titulo = (option.nombre || option.descripcion || option.mcp || '-').toString().trim();
  const mcp = (option.mcp ?? '').trim();
  return (
    <Box
      component="li"
      {...props}
      key={`${keyPrefix}-${mcp}-${(option.tipo ?? '').trim()}-${option.id ?? ''}`}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%' }}>
        <Icon sx={{ color: '#1976d2', fontSize: 22, flexShrink: 0 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={500} noWrap>
            {titulo}
          </Typography>
          {mcp && mcp !== titulo ? (
            <Typography variant="caption" color="text.secondary" display="block" noWrap>
              {mcp}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Box>
  );
}

interface TransferenciaPendiente {
  payload: any;
  mcpOrigen: string;
  mcpDestino: string;
  grupoDestinoNombre: string;
  detalle: string;
  observaciones: string;
  importe: number;
  grupoOrigen: number;
  grupoDestino: number;
  chequeCartera?: ChequeCartera;
  cuponCartera?: CuponCartera;
}

interface IngresosCarteraGrupo {
  cheques: ChequeCartera[];
  cupones: CuponCartera[];
}

/** Clave de exclusión en sesión: solo bloquea el ítem en la caja/grupo desde el que se traspasó. */
function claveCarteraUsadaSesion(grupoOrigen: number, nroCarga: number): string {
  return `${grupoOrigen}:${nroCarga}`;
}

function nroCargaCuponValido(value: unknown): number | null {
  if (value == null || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? num : null;
}

function mergeChequesPorNroCarga(
  base: ChequeCartera[],
  extras: ChequeCartera[]
): ChequeCartera[] {
  const map = new Map<number, ChequeCartera>();
  for (const c of base) {
    const nro = nroCargaCuponValido(c.nroCarga);
    if (nro != null) map.set(nro, c);
  }
  for (const c of extras) {
    const nro = nroCargaCuponValido(c.nroCarga);
    if (nro == null) continue;
    if (!map.has(nro)) map.set(nro, c);
  }
  return Array.from(map.values());
}

function mergeCuponesPorNroCarga(
  base: CuponCartera[],
  extras: CuponCartera[]
): CuponCartera[] {
  const map = new Map<number, CuponCartera>();
  for (const c of base) {
    const nro = nroCargaCuponValido(c.nroCarga);
    if (nro != null) map.set(nro, c);
  }
  for (const c of extras) {
    const nro = nroCargaCuponValido(c.nroCarga);
    if (nro == null) continue;
    if (!map.has(nro)) map.set(nro, c);
  }
  return Array.from(map.values());
}

function quitarNroCargaDeIngresos(
  prev: Record<number, IngresosCarteraGrupo>,
  grupo: number,
  nroCarga: number,
  tipo: 'cheque' | 'cupon'
): Record<number, IngresosCarteraGrupo> {
  const actual = prev[grupo];
  if (!actual) return prev;
  const nextGrupo: IngresosCarteraGrupo = {
    cheques:
      tipo === 'cheque'
        ? actual.cheques.filter((c) => nroCargaCuponValido(c.nroCarga) !== nroCarga)
        : actual.cheques,
    cupones:
      tipo === 'cupon'
        ? actual.cupones.filter((c) => nroCargaCuponValido(c.nroCarga) !== nroCarga)
        : actual.cupones,
  };
  return { ...prev, [grupo]: nextGrupo };
}

function agregarIngresoCartera(
  prev: Record<number, IngresosCarteraGrupo>,
  grupoDestino: number,
  grupoOrigen: number,
  cheque?: ChequeCartera,
  cupon?: CuponCartera
): Record<number, IngresosCarteraGrupo> {
  let next = { ...prev };
  if (cheque) {
    const nro = nroCargaCuponValido(cheque.nroCarga);
    if (nro != null) {
      next = quitarNroCargaDeIngresos(next, grupoOrigen, nro, 'cheque');
      const dest = next[grupoDestino] ?? { cheques: [], cupones: [] };
      const cheques = mergeChequesPorNroCarga(dest.cheques, [cheque]);
      next = { ...next, [grupoDestino]: { ...dest, cheques } };
    }
  }
  if (cupon) {
    const nro = nroCargaCuponValido(cupon.nroCarga);
    if (nro != null) {
      next = quitarNroCargaDeIngresos(next, grupoOrigen, nro, 'cupon');
      const dest = next[grupoDestino] ?? { cheques: [], cupones: [] };
      const cupones = mergeCuponesPorNroCarga(dest.cupones, [cupon]);
      next = { ...next, [grupoDestino]: { ...dest, cupones } };
    }
  }
  return next;
}

function extraerNroCargaChequePendiente(item: TransferenciaPendiente): number | null {
  return nroCargaCuponValido(
    item.payload?.medioCobroOrigen?.cheque?.nroCarga ??
      item.payload?.medioCobroDestino?.cheque?.nroCarga
  );
}

function extraerNroCargaCuponPendiente(item: TransferenciaPendiente): number | null {
  return nroCargaCuponValido(
    item.payload?.medioCobroOrigen?.tarjeta?.nroCarga ??
      item.payload?.medioCobroDestino?.tarjeta?.nroCarga
  );
}

const MSG_SIN_GRUPO_DESTINO = 'Seleccione el Grupo de Caja destino.';
const MSG_SIN_PLANILLA_ABIERTA = 'El grupo de caja destino no tiene una planilla de caja abierta.';
const MSG_CHEQUE_PROPIO_NO_TRASPASABLE = 'Los cheques propios no se pueden traspasar entre cajas.';
const MSG_MISMA_CAJA_DESTINO = 'El medio de pago destino no puede pertenecer a la misma caja que el origen.';

function esMedioChequePropio(medio?: MedioTransferencia | null): boolean {
  return (medio?.tipo ?? '').toString().trim().toUpperCase() === TIPO_CHEQUE_PROPIO;
}

function esTipoMcpConSaldoSugerido(tipo?: string | null): boolean {
  const t = (tipo ?? '').toString().trim().toUpperCase();
  return t === TIPO_EFECTIVO || t === TIPO_TRANSFERENCIA || t === TIPO_OTROS;
}

function montoSugeridoDesdeSaldo(saldo: number): string {
  const valor = Math.max(0, Number(saldo) || 0);
  if (valor === 0) return '0';
  return String(Math.round(valor * 100) / 100);
}

function obtenerSaldoMcpDesdeArqueo(
  porMcp: Array<{ mcp?: string; saldo?: number; Saldo?: number }> | undefined,
  mcpBuscado: string
): number {
  const codigo = mcpBuscado.trim().toUpperCase();
  if (!codigo) return 0;
  const fila = (porMcp ?? []).find((x) => (x.mcp ?? '').trim().toUpperCase() === codigo);
  if (!fila) return 0;
  return Number(fila.saldo ?? fila.Saldo ?? 0) || 0;
}

function normalizarClaseMcp(clase?: string | null): string {
  return (clase ?? '').toString().trim().toLowerCase();
}

function sugerirMedioDestinoPorClase(
  origen: MedioTransferencia | null,
  opciones: MedioTransferencia[]
): MedioTransferencia | null {
  if (!origen) return null;
  const claseOrigen = normalizarClaseMcp(origen.clase);
  if (!claseOrigen) return null;
  const coincidencias = opciones.filter(
    (m) => normalizarClaseMcp(m.clase) === claseOrigen
  );
  if (coincidencias.length !== 1) return null;
  return coincidencias[0];
}

function filtrarMediosTransferibles(medios: MedioTransferencia[]): MedioTransferencia[] {
  return medios.filter((m) => !esMedioChequePropio(m));
}

async function resolverGrupoUsuarioPorMcp(
  mcpCodigo: string,
  gruposUsuario: GrupoCajaChica[]
): Promise<GrupoCajaChica | null> {
  const mcp = mcpCodigo.trim();
  if (!mcp || gruposUsuario.length === 0) return null;

  let grupoCodigo: number | undefined;

  try {
    const det = await tesoreriaService.getMcpDetallePorNombreYTipo(mcp, TIPO_CHEQUE);
    grupoCodigo = det.grupo;
  } catch {
    /* intentar búsqueda */
  }

  if (grupoCodigo == null || !Number.isFinite(grupoCodigo) || grupoCodigo <= 0) {
    try {
      const busqueda = await mcpService.getMcps({ searchTerm: mcp, pageNumber: 1, pageSize: 50 });
      const exacto = (busqueda.items ?? []).find(
        (item) => (item.mcp ?? '').trim().toUpperCase() === mcp.toUpperCase()
      );
      grupoCodigo = exacto?.grupo ?? undefined;
    } catch {
      return null;
    }
  }

  if (grupoCodigo == null || !Number.isFinite(grupoCodigo) || grupoCodigo <= 0) return null;
  return gruposUsuario.find((g) => g.codigo === grupoCodigo) ?? null;
}

async function cajaAbiertaEnGrupo(grupo: number): Promise<boolean> {
  const caja = await tesoreriaService.getCajaAbiertaPorGrupo(grupo);
  const codigo = caja?.codigoCaja ?? caja?.nroCaja ?? caja?.codigo ?? null;
  return codigo != null && Number(codigo) > 0;
}

function persistGrupoCajaChica(grupo: GrupoCajaChica): void {
  try {
    localStorage.setItem('grupoCajaChicaSeleccionado', JSON.stringify(grupo));
    localStorage.setItem('grupoCajaMcp', String(grupo.codigo ?? ''));
  } catch {
    /* ignore */
  }
}

function buscarGrupoGuardadoEnLista(
  grupos: GrupoCajaChica[],
  saved: string | null
): GrupoCajaChica | null {
  if (!saved) return null;
  try {
    const parsed = JSON.parse(saved);
    const codigo = Number(parsed?.codigo ?? parsed?.id ?? null);
    if (!Number.isFinite(codigo) || codigo <= 0) return null;
    return grupos.find((g) => g.codigo === codigo || g.id === codigo) ?? null;
  } catch {
    return null;
  }
}

function mapGrupoMcpToCajaChica(grupo: GrupoMcp): GrupoCajaChica {
  return {
    id: grupo.grupoMcp,
    codigo: grupo.grupoMcp,
    nombre: grupo.descripcion?.trim() || `Grupo ${grupo.grupoMcp}`,
    grupoMcpDestino: grupo.grupoMcpDestino,
  };
}

const CHEQUES_TABLA_ROWS_PER_PAGE = 50;

function filtrarChequesCarteraPorBusqueda(cheques: ChequeCartera[], busqueda: string): ChequeCartera[] {
  const termino = busqueda.toLowerCase().trim();
  if (!termino) return cheques;
  const montoBusqueda = parseFloat(termino.replace(/[^\d.,]/g, '').replace(',', '.'));
  const buscaMonto = !Number.isNaN(montoBusqueda);
  return cheques.filter((cheque) => {
    const coincideTexto =
      cheque.nombreBanco?.toLowerCase().includes(termino) ||
      String(cheque.nroCheque).includes(termino) ||
      formatNroCheque(cheque.nroCheque).includes(termino) ||
      cheque.librador?.toLowerCase().includes(termino) ||
      cheque.nombreSucursal?.toLowerCase().includes(termino) ||
      cheque.concepto?.toLowerCase().includes(termino) ||
      String(cheque.nroSucursal).includes(termino) ||
      String(cheque.nroCuenta ?? '').toLowerCase().includes(termino);
    const coincideMonto =
      buscaMonto && Math.abs((cheque.importe ?? 0) - montoBusqueda) < 0.01;
    return coincideTexto || coincideMonto;
  });
}

interface FilaChequeCarteraTraspasoProps {
  cheque: ChequeCartera;
  estaSeleccionado: boolean;
  esFijoDesdeCartera: boolean;
  seleccionDeshabilitada: boolean;
  onToggle: (nroCarga: number, seleccionar: boolean) => void;
}

const FilaChequeCarteraTraspaso = React.memo(function FilaChequeCarteraTraspaso({
  cheque,
  estaSeleccionado,
  esFijoDesdeCartera,
  seleccionDeshabilitada,
  onToggle,
}: FilaChequeCarteraTraspasoProps) {
  return (
    <TableRow
      hover={!seleccionDeshabilitada}
      selected={estaSeleccionado}
      sx={{
        cursor: seleccionDeshabilitada ? 'default' : 'pointer',
        bgcolor: estaSeleccionado ? 'action.selected' : 'inherit',
        '&:hover': {
          bgcolor: seleccionDeshabilitada
            ? estaSeleccionado
              ? 'action.selected'
              : 'inherit'
            : estaSeleccionado
              ? 'action.selected'
              : 'action.hover',
        },
      }}
      onClick={() => onToggle(cheque.nroCarga, !estaSeleccionado)}
    >
      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
        <Tooltip
          title={
            esFijoDesdeCartera
              ? 'Fijado desde cartera. Agregá el traspaso para modificar la selección.'
              : seleccionDeshabilitada
                ? 'Selección bloqueada hasta agregar el traspaso.'
                : ''
          }
          disableHoverListener={!seleccionDeshabilitada}
        >
          <span>
            <Checkbox
              checked={estaSeleccionado}
              disabled={seleccionDeshabilitada}
              onChange={(e) => onToggle(cheque.nroCarga, e.target.checked)}
            />
          </span>
        </Tooltip>
      </TableCell>
      <TableCell>{cheque.nombreBanco}</TableCell>
      <TableCell>{formatNroChequeVista(cheque.nroCheque)}</TableCell>
      <TableCell>
        {cheque.nombreSucursal} ({cheque.nroSucursal})
      </TableCell>
      <TableCell align="right" sx={{ fontWeight: 600, color: 'primary.main' }}>
        {formatCurrency(cheque.importe ?? 0)}
      </TableCell>
      <TableCell>{cheque.librador}</TableCell>
      <TableCell>
        {cheque.fechaEmision ? new Date(cheque.fechaEmision).toLocaleDateString() : '-'}
      </TableCell>
      <TableCell>
        {cheque.fechaVencimiento ? new Date(cheque.fechaVencimiento).toLocaleDateString() : '-'}
      </TableCell>
    </TableRow>
  );
});

const TesoreriaTransferencias: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const preseleccionDesdeCarteraRef = useRef(false);
  const mcpOrigenPreseleccionadoRef = useRef('');
  const nrosCargaPreseleccionadosRef = useRef<number[]>([]);
  const mcpOrigenAplicadoRef = useRef(false);
  const chequesPreseleccionadosAplicadosRef = useRef(false);
  const grupoMcpResueltoRef = useRef(false);

  const [grupos, setGrupos] = useState<GrupoCajaChica[]>([]);
  const [gruposDestinoTodos, setGruposDestinoTodos] = useState<GrupoCajaChica[]>([]);
  const [grupoSeleccionado, setGrupoSeleccionado] = useState<GrupoCajaChica | null>(null);
  const [grupoDestinoSeleccionado, setGrupoDestinoSeleccionado] = useState<GrupoCajaChica | null>(null);
  const [mcpsPorGrupo, setMcpsPorGrupo] = useState<MedioTransferencia[]>([]);
  const [loadingGrupos, setLoadingGrupos] = useState(false);
  const [loadingGruposDestino, setLoadingGruposDestino] = useState(false);
  const [loadingMcps, setLoadingMcps] = useState(false);

  const [chequeraOrigen, setChequeraOrigen] = useState<MedioTransferencia | null>(null);
  const [origenAutocompleteAbierto, setOrigenAutocompleteAbierto] = useState(false);
  const [chequeraDestino, setChequeraDestino] = useState<MedioTransferencia | null>(null);
  const [destinoAutocompleteAbierto, setDestinoAutocompleteAbierto] = useState(false);
  const [mcpsDestinoPorTipo, setMcpsDestinoPorTipo] = useState<MedioTransferencia[]>([]);
  const [loadingMcpsDestino, setLoadingMcpsDestino] = useState(false);
  const [detalle, setDetalle] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [monto, setMonto] = useState<string>('');

  const [chequesCartera, setChequesCartera] = useState<ChequeCartera[]>([]);
  const [cuponesCartera, setCuponesCartera] = useState<CuponCartera[]>([]);
  const [loadingCuponesCartera, setLoadingCuponesCartera] = useState(false);
  const [cuponesCarteraSeleccionados, setCuponesCarteraSeleccionados] = useState<Set<number>>(new Set());
  const [busquedaCupon, setBusquedaCupon] = useState('');
  const [orderByCupon, setOrderByCupon] = useState<keyof CuponCartera | ''>('');
  const [orderCupon, setOrderCupon] = useState<'asc' | 'desc'>('asc');
  const [loadingChequesCartera, setLoadingChequesCartera] = useState(false);
  const [chequesCarteraSeleccionados, setChequesCarteraSeleccionados] = useState<Set<number>>(new Set());
  /** Cheques traídos desde cartera: no se pueden deseleccionar hasta agregar al listado. */
  const [chequesFijosDesdeCartera, setChequesFijosDesdeCartera] = useState<Set<number>>(new Set());
  const chequesFijosDesdeCarteraRef = useRef<Set<number>>(new Set());
  chequesFijosDesdeCarteraRef.current = chequesFijosDesdeCartera;
  const chequesCarteraCacheKeyRef = useRef('');
  const chequesCarteraCargadosRef = useRef(false);
  /** nroCarga ya guardados en esta sesión, por grupo origen (`grupo:nroCarga`). */
  const [clavesChequesUsadosSesion, setClavesChequesUsadosSesion] = useState<Set<string>>(
    () => new Set()
  );
  const [clavesCuponesUsadosSesion, setClavesCuponesUsadosSesion] = useState<Set<string>>(
    () => new Set()
  );
  /** Ítems que esta sesión traspasó HACIA cada grupo (para verlos sin salir de la pantalla). */
  const [ingresosCarteraPorGrupo, setIngresosCarteraPorGrupo] = useState<
    Record<number, IngresosCarteraGrupo>
  >({});
  /** Se incrementa al guardar / abrir formulario para forzar refetch de cartera. */
  const [carteraReloadToken, setCarteraReloadToken] = useState(0);
  const planillaDestinoCacheRef = useRef<Map<number, boolean>>(new Map());
  const [chequesTablaPage, setChequesTablaPage] = useState(0);
  const [chequesTablaRowsPerPage, setChequesTablaRowsPerPage] = useState(CHEQUES_TABLA_ROWS_PER_PAGE);
  const [cargaChequesProgress, setCargaChequesProgress] = useState<ExportProgressState>({
    message: '',
    currentPage: 0,
    totalPages: 0,
  });
  const [busquedaCheque, setBusquedaCheque] = useState('');
  const [orderByCheque, setOrderByCheque] = useState<keyof ChequeCartera | ''>('');
  const [orderCheque, setOrderCheque] = useState<'asc' | 'desc'>('asc');

  const [sucursalesBancoCheque, setSucursalesBancoCheque] = useState<SucursalBancoRow[]>([]);
  const [filtroChequeFechaIngresoDesde, setFiltroChequeFechaIngresoDesde] = useState('');
  const [filtroChequeFechaIngresoHasta, setFiltroChequeFechaIngresoHasta] = useState('');
  const [filtroChequeFechaVencDesde, setFiltroChequeFechaVencDesde] = useState('');
  const [filtroChequeFechaVencHasta, setFiltroChequeFechaVencHasta] = useState('');
  const [filtroChequeNroBanco, setFiltroChequeNroBanco] = useState('');
  const [filtroChequeNroSucursal, setFiltroChequeNroSucursal] = useState('');
  const [filtroChequeImporteDesde, setFiltroChequeImporteDesde] = useState('');
  const [filtroChequeImporteHasta, setFiltroChequeImporteHasta] = useState('');
  const [filtroChequeEstado, setFiltroChequeEstado] = useState('');
  const [filtroChequeLibrador, setFiltroChequeLibrador] = useState('');

  const sucursalesChequeFiltradas = useMemo(() => {
    const nb = parseInt(filtroChequeNroBanco, 10);
    if (!Number.isFinite(nb) || nb <= 0) return [];
    return sucursalesBancoCheque.filter((s) => s.nroBanco === nb);
  }, [sucursalesBancoCheque, filtroChequeNroBanco]);

  const buildChequesCarteraQuery = useCallback((): ChequesCarteraQueryParams => {
    const q: ChequesCarteraQueryParams = {};
    const mcpOrigen = (chequeraOrigen?.mcp ?? '').trim();
    if (mcpOrigen) q.mcp = mcpOrigen;
    const nb = parseInt(filtroChequeNroBanco, 10);
    if (Number.isFinite(nb) && nb > 0) q.nroBanco = nb;
    const ns = parseInt(filtroChequeNroSucursal, 10);
    if (Number.isFinite(ns) && ns > 0) q.nroSucursal = ns;
    if (filtroChequeFechaIngresoDesde.trim()) q.fechaDesde = filtroChequeFechaIngresoDesde.trim();
    if (filtroChequeFechaIngresoHasta.trim()) q.fechaHasta = filtroChequeFechaIngresoHasta.trim();
    if (filtroChequeFechaVencDesde.trim()) q.fechaVencimientoDesde = filtroChequeFechaVencDesde.trim();
    if (filtroChequeFechaVencHasta.trim()) q.fechaVencimientoHasta = filtroChequeFechaVencHasta.trim();
    const ides = parseFloat(filtroChequeImporteDesde.replace(',', '.'));
    if (!Number.isNaN(ides)) q.importeDesde = ides;
    const ihas = parseFloat(filtroChequeImporteHasta.replace(',', '.'));
    if (!Number.isNaN(ihas)) q.importeHasta = ihas;
    if (filtroChequeEstado.trim()) q.estado = filtroChequeEstado.trim();
    if (filtroChequeLibrador.trim()) q.librador = filtroChequeLibrador.trim();
    return q;
  }, [
    filtroChequeNroBanco,
    filtroChequeNroSucursal,
    filtroChequeFechaIngresoDesde,
    filtroChequeFechaIngresoHasta,
    filtroChequeFechaVencDesde,
    filtroChequeFechaVencHasta,
    filtroChequeImporteDesde,
    filtroChequeImporteHasta,
    filtroChequeEstado,
    filtroChequeLibrador,
    chequeraOrigen?.mcp,
  ]);

  const getChequesCarteraCacheKey = useCallback((): string => {
    if (!grupoSeleccionado?.codigo) return '';
    const mcpOrigen = (chequeraOrigen?.mcp ?? '').trim();
    if (!mcpOrigen) return '';
    return `${grupoSeleccionado.codigo}|${mcpOrigen}|${carteraReloadToken}|${JSON.stringify(buildChequesCarteraQuery())}`;
  }, [grupoSeleccionado?.codigo, chequeraOrigen?.mcp, buildChequesCarteraQuery, carteraReloadToken]);

  const resetChequesCartera = useCallback(() => {
    chequesCarteraCacheKeyRef.current = '';
    chequesCarteraCargadosRef.current = false;
    setChequesCartera([]);
    setChequesCarteraSeleccionados(new Set());
    setChequesFijosDesdeCartera(new Set());
  }, []);

  const resetCuponesCartera = useCallback(() => {
    setCuponesCartera([]);
    setCuponesCarteraSeleccionados(new Set());
    setBusquedaCupon('');
  }, []);

  const limpiarFiltrosAvanzadosCheques = useCallback(() => {
    setFiltroChequeFechaIngresoDesde('');
    setFiltroChequeFechaIngresoHasta('');
    setFiltroChequeFechaVencDesde('');
    setFiltroChequeFechaVencHasta('');
    setFiltroChequeNroBanco('');
    setFiltroChequeNroSucursal('');
    setFiltroChequeImporteDesde('');
    setFiltroChequeImporteHasta('');
    setFiltroChequeEstado('');
    setFiltroChequeLibrador('');
  }, []);

  const { sucursales, loading: loadingSucursales } = useSucursalesUsuarioContext();
  const [nroSucSeleccionado, setNroSucSeleccionado] = useState<number>(0);
  const [fechaEmisionForm, setFechaEmisionForm] = useState<string>(() =>
    new Date().toISOString().split('T')[0]
  );

  const [transferencias, setTransferencias] = useState<TransferenciaItem[]>([]);
  const [transferenciasPendientes, setTransferenciasPendientes] = useState<TransferenciaPendiente[]>([]);
  const [loadingListado, setLoadingListado] = useState(false);
  const [guardandoTodas, setGuardandoTodas] = useState(false);
  const [mostrarFormularioNueva, setMostrarFormularioNueva] = useState(false);
  const [openGrupoCajaDialog, setOpenGrupoCajaDialog] = useState(false);
  const [grupoPendienteId, setGrupoPendienteId] = useState<number | null>(null);
  const loadStoredListado = () =>
    loadTesoreriaFiltros<TransferenciasListadoFiltrosStored>(TESORERIA_FILTROS_KEYS.transferencias);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => storedNumber(loadStoredListado(), 'rowsPerPage', 10));
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState(() => storedString(loadStoredListado(), 'searchTerm', ''));
  const [fechaDesde, setFechaDesde] = useState<string>(() => {
    const guardado = loadStoredListado().fechaDesde;
    if (guardado) return guardado;
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  });
  const [fechaHasta, setFechaHasta] = useState<string>(() => {
    const guardado = loadStoredListado().fechaHasta;
    if (guardado) return guardado;
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
  });
  const { filtroNroSuc, setFiltroNroSuc, sucursales: sucursalesVentaFiltro, loadingSucursales: loadingSucursalesVentaFiltro, filtroListo } = useSucursalesVentaFiltro({
    resetPreferidaOnNavigation: true,
  });
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [selectedTransferenciaId, setSelectedTransferenciaId] = useState<number | null>(null);

  const { onSucursalChange } = useSucursalAltaFormulario({
    nroSuc: nroSucSeleccionado,
    setNroSuc: setNroSucSeleccionado,
    sucursales,
    loadingSucursales,
    enabled: mostrarFormularioNueva,
  });

  const tipoDestino = chequeraDestino?.tipo ? String(chequeraDestino.tipo).trim().toUpperCase() : '';
  const esEfectivo = tipoDestino === TIPO_EFECTIVO;
  const esCheque = tipoDestino === TIPO_CHEQUE;
  const esDestinoTarjeta =
    tipoDestino === TIPO_TARJETA ||
    (!!tipoDestino && tipoDestino.startsWith('T') && tipoDestino !== TIPO_TRANSFERENCIA);
  const esTransferenciaBancaria = tipoDestino === TIPO_TRANSFERENCIA;
  const esOtros = tipoDestino === TIPO_OTROS || tipoDestino === TIPO_RENDICION;

  // Tipo de la chequera origen (campo "tipo", comparación normalizada con trim)
  const tipoOrigen = (chequeraOrigen?.tipo ?? '').toString().trim().toUpperCase();

  // Opciones destino: MCPs del grupo destino seleccionado, excluyendo el mismo MCP que el origen
  const opcionesDestino = useMemo(() => {
    if (!chequeraOrigen || !tipoOrigen) return [];
    return filtrarMediosTransferibles(mcpsDestinoPorTipo).filter((m) => {
      return (m.mcp ?? '').trim() !== (chequeraOrigen?.mcp ?? '').trim();
    });
  }, [chequeraOrigen, tipoOrigen, mcpsDestinoPorTipo]);

  const gruposDestinoOpciones = useMemo(() => {
    const origenCodigo = grupoSeleccionado?.codigo;
    const map = new Map<number, GrupoCajaChica>();
    for (const g of gruposDestinoTodos) {
      if (g.codigo !== origenCodigo) {
        map.set(g.codigo, g);
      }
    }
    if (
      grupoDestinoSeleccionado &&
      grupoDestinoSeleccionado.codigo !== origenCodigo &&
      !map.has(grupoDestinoSeleccionado.codigo)
    ) {
      map.set(grupoDestinoSeleccionado.codigo, grupoDestinoSeleccionado);
    }
    return Array.from(map.values()).sort((a, b) =>
      (a.nombre || `Grupo ${a.codigo}`).localeCompare(b.nombre || `Grupo ${b.codigo}`, 'es')
    );
  }, [gruposDestinoTodos, grupoSeleccionado?.codigo, grupoDestinoSeleccionado]);

  const grupoDestinoBloqueado = useMemo(() => {
    const codigo = grupoSeleccionado?.grupoMcpDestino;
    return codigo != null && Number(codigo) > 0;
  }, [grupoSeleccionado?.grupoMcpDestino]);

  const hayTraspasosPendientes = transferenciasPendientes.length > 0;

  const mcpsOrigenOrdenados = useMemo(
    () => filtrarMediosTransferibles(mcpsPorGrupo).sort(compararMediosPorCategoria),
    [mcpsPorGrupo]
  );

  const opcionesDestinoOrdenadas = useMemo(
    () => [...opcionesDestino].sort(compararMediosPorCategoria),
    [opcionesDestino]
  );

  const cargarGrupos = useCallback(async () => {
    setLoadingGrupos(true);
    try {
      const list = await tesoreriaService.getGruposCajaChica();
      setGrupos(list || []);
    } catch (error) {
      console.error('Error al cargar grupos:', error);
      enqueueSnackbar('Error al cargar los grupos de caja', { variant: 'error' });
      setGrupos([]);
    } finally {
      setLoadingGrupos(false);
    }
  }, [enqueueSnackbar]);

  const cargarGruposDestino = useCallback(async () => {
    setLoadingGruposDestino(true);
    try {
      const all: GrupoMcp[] = [];
      let pageNum = 1;
      let totalPages = 1;
      do {
        const res = await grupoMcpService.getGruposMcp({ pageNumber: pageNum, pageSize: 50 });
        all.push(...(res.items || []));
        totalPages = res.totalPages;
        pageNum += 1;
      } while (pageNum <= totalPages);
      setGruposDestinoTodos(all.map(mapGrupoMcpToCajaChica));
    } catch (error) {
      console.error('Error al cargar grupos destino:', error);
      enqueueSnackbar('Error al cargar los grupos de caja destino', { variant: 'error' });
      setGruposDestinoTodos([]);
    } finally {
      setLoadingGruposDestino(false);
    }
  }, [enqueueSnackbar]);

  const resolverGrupoDestino = useCallback(
    async (codigoDestino: number): Promise<GrupoCajaChica | null> => {
      const enListaDestino = gruposDestinoTodos.find((g) => g.codigo === codigoDestino);
      if (enListaDestino) return enListaDestino;
      const enListaUsuario = grupos.find((g) => g.codigo === codigoDestino);
      if (enListaUsuario) return enListaUsuario;
      try {
        const det = await grupoMcpService.getGrupoMcp(codigoDestino);
        return mapGrupoMcpToCajaChica(det);
      } catch {
        return null;
      }
    },
    [grupos, gruposDestinoTodos]
  );

  const resetAltaTraspaso = useCallback(() => {
    setChequeraOrigen(null);
    setChequeraDestino(null);
    setGrupoDestinoSeleccionado(null);
    setMcpsDestinoPorTipo([]);
    setDetalle('');
    setObservaciones('');
    setMonto('');
    setTransferenciasPendientes([]);
    setFechaEmisionForm(new Date().toISOString().split('T')[0]);
    setChequesTablaPage(0);
    setBusquedaCheque('');
    setOrderByCheque('');
    setOrderCheque('asc');
    setBusquedaCupon('');
    setOrderByCupon('');
    setOrderCupon('asc');
    setClavesChequesUsadosSesion(new Set());
    setClavesCuponesUsadosSesion(new Set());
    planillaDestinoCacheRef.current.clear();
    mcpOrigenAplicadoRef.current = false;
    chequesPreseleccionadosAplicadosRef.current = false;
    grupoMcpResueltoRef.current = false;
    mcpOrigenPreseleccionadoRef.current = '';
    nrosCargaPreseleccionadosRef.current = [];
    resetChequesCartera();
    resetCuponesCartera();
    limpiarFiltrosAvanzadosCheques();
  }, [resetChequesCartera, resetCuponesCartera, limpiarFiltrosAvanzadosCheques]);

  useEffect(() => {
    cargarGrupos();
    cargarGruposDestino();
  }, [cargarGrupos, cargarGruposDestino]);

  // Preselección desde ChequesTercerosCartera: abre nuevo traspaso con MCP y cheques
  useEffect(() => {
    if (preseleccionDesdeCarteraRef.current) return;

    const state = (location.state ?? {}) as {
      abrirNuevoTraspaso?: boolean;
      nrosCargaPreseleccionados?: number[];
      chequesPreseleccionados?: ChequeCartera[];
      mcpOrigenPreseleccionado?: string;
    };

    if (!state.abrirNuevoTraspaso) return;

    const chequesRaw = state.chequesPreseleccionados;
    const cheques = Array.isArray(chequesRaw)
      ? chequesRaw.filter((c) => Number(c?.nroCarga) > 0 && esChequeDisponibleEnCartera(c))
      : [];
    const nros = (state.nrosCargaPreseleccionados ?? [])
      .map((n) => Number(n))
      .filter((n) => Number.isFinite(n) && n > 0);
    const mcpOrigen = (state.mcpOrigenPreseleccionado ?? '').trim();

    if (cheques.length === 0 && nros.length === 0) return;

    resetAltaTraspaso();
    preseleccionDesdeCarteraRef.current = true;
    mcpOrigenAplicadoRef.current = false;
    chequesPreseleccionadosAplicadosRef.current = false;
    grupoMcpResueltoRef.current = false;
    mcpOrigenPreseleccionadoRef.current = mcpOrigen || cheques[0]?.mcp?.trim() || '';
    nrosCargaPreseleccionadosRef.current = cheques.length > 0 ? cheques.map((c) => c.nroCarga) : nros;

    setCarteraReloadToken((n) => n + 1);
    setMostrarFormularioNueva(true);

    const cantidad = nrosCargaPreseleccionadosRef.current.length;
    enqueueSnackbar(
      `${cantidad} cheque${cantidad > 1 ? 's' : ''} listo${cantidad > 1 ? 's' : ''} para traspasar. Completá el grupo destino y confirmá el traspaso.`,
      { variant: 'info', autoHideDuration: 8000 }
    );

    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate, enqueueSnackbar, resetAltaTraspaso]);

  // Al abrir nuevo traspaso: restaurar grupo, auto-seleccionar si hay uno solo, o modal
  useEffect(() => {
    if (!mostrarFormularioNueva || loadingGrupos) return;
    if (grupoSeleccionado != null) return;
    if (openGrupoCajaDialog) return;
    // Si viene desde cartera, el grupo se resuelve por MCP (no usar el guardado en localStorage).
    if (mcpOrigenPreseleccionadoRef.current.trim()) return;

    const saved = localStorage.getItem('grupoCajaChicaSeleccionado');
    const matchGuardado = buscarGrupoGuardadoEnLista(grupos, saved);
    if (matchGuardado) {
      setGrupoSeleccionado(matchGuardado);
      return;
    }

    if (grupos.length === 1) {
      const unico = grupos[0];
      setGrupoSeleccionado(unico);
      persistGrupoCajaChica(unico);
      return;
    }

    if (grupos.length > 1) {
      setGrupoPendienteId(null);
      setOpenGrupoCajaDialog(true);
      return;
    }

    enqueueSnackbar('No tiene grupos de caja asignados. Contacte al administrador.', {
      variant: 'warning',
    });
    setMostrarFormularioNueva(false);
  }, [
    mostrarFormularioNueva,
    loadingGrupos,
    grupos,
    grupoSeleccionado,
    openGrupoCajaDialog,
    enqueueSnackbar,
  ]);

  // Desde cartera: ajustar el grupo origen al que pertenece el MCP del cheque
  useEffect(() => {
    const mcpBuscado = mcpOrigenPreseleccionadoRef.current.trim();
    if (!mostrarFormularioNueva || loadingGrupos || grupos.length === 0 || !mcpBuscado) return;
    if (grupoMcpResueltoRef.current) return;

    let cancelled = false;
    (async () => {
      const grupo = await resolverGrupoUsuarioPorMcp(mcpBuscado, grupos);
      if (cancelled) return;

      grupoMcpResueltoRef.current = true;

      if (!grupo) {
        enqueueSnackbar(
          `No se pudo ubicar el grupo de caja del MCP «${mcpBuscado}». Seleccioná el grupo origen manualmente.`,
          { variant: 'warning', autoHideDuration: 8000 }
        );
        if (grupos.length > 1) {
          setGrupoPendienteId(null);
          setOpenGrupoCajaDialog(true);
        } else if (grupos.length === 1) {
          setGrupoSeleccionado(grupos[0]);
          persistGrupoCajaChica(grupos[0]);
        }
        return;
      }

      if (grupoSeleccionado?.codigo !== grupo.codigo) {
        setGrupoSeleccionado(grupo);
        persistGrupoCajaChica(grupo);
        mcpOrigenAplicadoRef.current = false;
        chequesPreseleccionadosAplicadosRef.current = false;
        enqueueSnackbar(
          `Grupo origen ajustado a «${grupo.nombre}» según el MCP del cheque.`,
          { variant: 'info', autoHideDuration: 6000 }
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    mostrarFormularioNueva,
    loadingGrupos,
    grupos,
    grupoSeleccionado?.codigo,
    enqueueSnackbar,
  ]);

  useEffect(() => {
    if (grupoSeleccionado && openGrupoCajaDialog) {
      setOpenGrupoCajaDialog(false);
    }
  }, [grupoSeleccionado, openGrupoCajaDialog]);

  const handleConfirmarGrupoCaja = () => {
    if (grupoPendienteId == null) return;
    const selected = grupos.find((g) => g.id === grupoPendienteId || g.codigo === grupoPendienteId);
    if (!selected) return;
    setGrupoSeleccionado(selected);
    persistGrupoCajaChica(selected);
    setOpenGrupoCajaDialog(false);
  };

  const handleCancelarGrupoCaja = () => {
    setOpenGrupoCajaDialog(false);
    setGrupoPendienteId(null);
    resetAltaTraspaso();
    setMostrarFormularioNueva(false);
  };

  const confirmarDescartePendientes = () => {
    if (transferenciasPendientes.length === 0) return true;
    return window.confirm('Hay traspasos sin guardar. ¿Descartarlos?');
  };

  const handleToggleFormularioNueva = () => {
    if (mostrarFormularioNueva) {
      if (!confirmarDescartePendientes()) return;
      setOpenGrupoCajaDialog(false);
      resetAltaTraspaso();
      setMostrarFormularioNueva(false);
      return;
    }
    resetAltaTraspaso();
    setCarteraReloadToken((n) => n + 1);
    setMostrarFormularioNueva(true);
  };

  // Al entrar a la pantalla, partir de disponibilidad limpia para recalcular cupones/cheques desde API.
  useEffect(() => {
    setClavesCuponesUsadosSesion(new Set());
    setClavesChequesUsadosSesion(new Set());
    setIngresosCarteraPorGrupo({});
    setCarteraReloadToken((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!grupoSeleccionado?.codigo) {
      setMcpsPorGrupo([]);
      setChequeraOrigen(null);
      setChequeraDestino(null);
      setMcpsDestinoPorTipo([]);
      setGrupoDestinoSeleccionado(null);
      return;
    }
    setLoadingMcps(true);
    setChequeraOrigen(null);
    setChequeraDestino(null);
    setMcpsDestinoPorTipo([]);
    mcpOrigenAplicadoRef.current = false;
    chequesPreseleccionadosAplicadosRef.current = false;
    tesoreriaService
      .getMcpsPorGrupo(grupoSeleccionado.codigo)
      .then((list) => setMcpsPorGrupo(filtrarMediosTransferibles(list || [])))
      .catch((err) => {
        console.error('Error al cargar MCPs:', err);
        enqueueSnackbar('Error al cargar medios de pago', { variant: 'error' });
        setMcpsPorGrupo([]);
      })
      .finally(() => setLoadingMcps(false));
  }, [grupoSeleccionado?.codigo, enqueueSnackbar]);

  useEffect(() => {
    if (mcpOrigenAplicadoRef.current) return;
    const mcpBuscado = mcpOrigenPreseleccionadoRef.current.trim();
    if (!mcpBuscado || loadingMcps || !grupoSeleccionado || mcpsPorGrupo.length === 0) return;

    const match = mcpsPorGrupo.find(
      (m) => (m.mcp ?? '').trim().toUpperCase() === mcpBuscado.toUpperCase()
    );

    if (match) {
      mcpOrigenAplicadoRef.current = true;
      mcpOrigenPreseleccionadoRef.current = '';
      setChequeraOrigen(match);
      return;
    }

    if (loadingMcps) return;

    let cancelled = false;
    (async () => {
      const grupo = await resolverGrupoUsuarioPorMcp(mcpBuscado, grupos);
      if (cancelled) return;

      if (grupo && grupo.codigo !== grupoSeleccionado.codigo) {
        mcpOrigenAplicadoRef.current = false;
        setGrupoSeleccionado(grupo);
        persistGrupoCajaChica(grupo);
        enqueueSnackbar(
          `«${mcpBuscado}» pertenece al grupo «${grupo.nombre}». Se cambió el grupo origen.`,
          { variant: 'info', autoHideDuration: 7000 }
        );
        return;
      }

      mcpOrigenAplicadoRef.current = true;
      mcpOrigenPreseleccionadoRef.current = '';
      enqueueSnackbar(
        `No se encontró «${mcpBuscado}» en el grupo «${grupoSeleccionado.nombre || grupoSeleccionado.codigo}». Verificá que el grupo origen sea el correcto.`,
        { variant: 'warning', autoHideDuration: 9000 }
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [mcpsPorGrupo, loadingMcps, grupoSeleccionado, grupos, enqueueSnackbar]);

  useEffect(() => {
    if (!mostrarFormularioNueva) return;
    if (!grupoSeleccionado?.codigo) {
      setGrupoDestinoSeleccionado(null);
      return;
    }

    setChequeraDestino(null);
    const codigoSugerido = grupoSeleccionado.grupoMcpDestino;
    if (!codigoSugerido || codigoSugerido <= 0) {
      setGrupoDestinoSeleccionado(null);
      return;
    }

    let cancelled = false;
    (async () => {
      const sugerido = await resolverGrupoDestino(codigoSugerido);
      if (!cancelled) {
        setGrupoDestinoSeleccionado(sugerido);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    mostrarFormularioNueva,
    grupoSeleccionado?.codigo,
    grupoSeleccionado?.grupoMcpDestino,
    resolverGrupoDestino,
  ]);

  useEffect(() => {
    if (!chequeraOrigen || !tipoOrigen || !grupoDestinoSeleccionado?.codigo) {
      setMcpsDestinoPorTipo([]);
      return;
    }
    setLoadingMcpsDestino(true);
    setMcpsDestinoPorTipo([]);
    const tipoNorm = tipoOrigen.trim().toUpperCase();
    tesoreriaService
      .getMcpsPorGrupo(grupoDestinoSeleccionado.codigo)
      .then((list) =>
        setMcpsDestinoPorTipo(
          filtrarMediosTransferibles(list || []).filter(
            (m) => (m.tipo ?? '').toString().trim().toUpperCase() === tipoNorm
          )
        )
      )
      .catch((err) => {
        console.error('Error al cargar MCPs del grupo destino:', err);
        enqueueSnackbar('Error al cargar medios de pago del grupo destino', { variant: 'error' });
        setMcpsDestinoPorTipo([]);
      })
      .finally(() => setLoadingMcpsDestino(false));
  }, [chequeraOrigen, tipoOrigen, grupoDestinoSeleccionado?.codigo, enqueueSnackbar]);

  useEffect(() => {
    if (!mostrarFormularioNueva) return;
    if (
      !chequeraOrigen ||
      chequeraDestino ||
      loadingMcpsDestino ||
      opcionesDestino.length === 0
    ) {
      return;
    }
    const sugerido = sugerirMedioDestinoPorClase(chequeraOrigen, opcionesDestino);
    if (sugerido) {
      setChequeraDestino(sugerido);
    }
  }, [chequeraOrigen, chequeraDestino, opcionesDestino, loadingMcpsDestino, mostrarFormularioNueva]);

  const esOrigenChequesTercero = tipoOrigen === TIPO_CHEQUE;
  const esOrigenTarjeta =
    tipoOrigen === TIPO_TARJETA ||
    (tipoOrigen.startsWith('T') && tipoOrigen !== TIPO_TRANSFERENCIA);
  const necesitaSeleccionarCheque = esCheque || esOrigenChequesTercero;
  const necesitaSeleccionarCupon = esOrigenTarjeta || esDestinoTarjeta;
  const mostrarSeleccionCupones = necesitaSeleccionarCupon && (esOrigenTarjeta || !necesitaSeleccionarCheque);
  const mostrarSeleccionCheques = necesitaSeleccionarCheque && !mostrarSeleccionCupones;
  const necesitaSeleccionItemCartera = mostrarSeleccionCheques || mostrarSeleccionCupones;
  const seleccionChequesBloqueadaDesdeCartera = chequesFijosDesdeCartera.size > 0;

  const puedeModificarSeleccionCheque = useCallback(
    (nroCarga: number, seleccionar: boolean): boolean => {
      if (!seleccionChequesBloqueadaDesdeCartera) return true;
      if (chequesFijosDesdeCartera.has(nroCarga) && !seleccionar) return false;
      if (!chequesFijosDesdeCartera.has(nroCarga) && seleccionar) return false;
      return true;
    },
    [seleccionChequesBloqueadaDesdeCartera, chequesFijosDesdeCartera]
  );

  const actualizarSeleccionCheque = useCallback(
    (nroCarga: number, seleccionar: boolean) => {
      if (!puedeModificarSeleccionCheque(nroCarga, seleccionar)) return;
      startTransition(() => {
        setChequesCarteraSeleccionados((prev) => {
          const next = new Set(prev);
          if (seleccionar) next.add(nroCarga);
          else next.delete(nroCarga);
          return next;
        });
      });
    },
    [puedeModificarSeleccionCheque]
  );

  useEffect(() => {
    if (chequesPreseleccionadosAplicadosRef.current) return;
    const nros = nrosCargaPreseleccionadosRef.current;
    if (nros.length === 0 || loadingChequesCartera || !mostrarSeleccionCheques) return;

    const disponibles = new Set(chequesCartera.map((c) => c.nroCarga));
    const aSeleccionar = nros.filter((n) => disponibles.has(n));
    if (aSeleccionar.length === 0) {
      if (chequesCartera.length > 0) {
        chequesPreseleccionadosAplicadosRef.current = true;
        nrosCargaPreseleccionadosRef.current = [];
        enqueueSnackbar(
          'No se encontraron los cheques preseleccionados en la cartera de la caja origen.',
          { variant: 'warning' }
        );
      }
      return;
    }

    chequesPreseleccionadosAplicadosRef.current = true;
    nrosCargaPreseleccionadosRef.current = [];
    const seleccion = new Set(aSeleccionar);
    setChequesCarteraSeleccionados(seleccion);
    setChequesFijosDesdeCartera(seleccion);
    if (aSeleccionar.length < nros.length) {
      enqueueSnackbar(
        `Se preseleccionaron ${aSeleccionar.length} de ${nros.length} cheque(s) en el listado.`,
        { variant: 'info' }
      );
    }
  }, [
    chequesCartera,
    loadingChequesCartera,
    mostrarSeleccionCheques,
    enqueueSnackbar,
  ]);

  const sugiereMontoPorSaldoOrigen =
    esTipoMcpConSaldoSugerido(tipoOrigen) && !necesitaSeleccionItemCartera;

  const cargarMontoSugeridoOrigen = useCallback(async () => {
    if (!chequeraOrigen?.mcp || !grupoSeleccionado?.codigo || !sugiereMontoPorSaldoOrigen) {
      return;
    }
    const mcpOrigen = (chequeraOrigen.mcp ?? '').trim().toUpperCase();
    try {
      const caja = await tesoreriaService.getCajaAbiertaPorGrupo(grupoSeleccionado.codigo);
      const codigoCaja =
        (caja as { codigoCaja?: number; nroCaja?: number; codigo?: number })?.codigoCaja ??
        (caja as { codigoCaja?: number; nroCaja?: number; codigo?: number })?.nroCaja ??
        (caja as { codigoCaja?: number; nroCaja?: number; codigo?: number })?.codigo;
      if (!codigoCaja) {
        setMonto('0');
        return;
      }
      const arqueo = await tesoreriaCajaService.getInformacionArqueo(Number(codigoCaja));
      const saldoArqueo = obtenerSaldoMcpDesdeArqueo(arqueo.porMcp, chequeraOrigen.mcp ?? '');
      const importePendienteMismoOrigen = transferenciasPendientes.reduce((sum, t) => {
        if ((t.mcpOrigen ?? '').trim().toUpperCase() !== mcpOrigen) return sum;
        return sum + (Number(t.importe) || 0);
      }, 0);
      const disponible = Math.max(0, (Number(saldoArqueo) || 0) - importePendienteMismoOrigen);
      setMonto(montoSugeridoDesdeSaldo(disponible));
    } catch (error) {
      console.error('Error al obtener saldo del MCP origen:', error);
      setMonto('0');
    }
  }, [
    chequeraOrigen?.mcp,
    grupoSeleccionado?.codigo,
    sugiereMontoPorSaldoOrigen,
    transferenciasPendientes,
  ]);

  useEffect(() => {
    if (!mostrarFormularioNueva) {
      return;
    }

    if (!chequeraOrigen?.mcp || !grupoSeleccionado?.codigo) {
      if (!necesitaSeleccionItemCartera) setMonto('');
      return;
    }

    if (!sugiereMontoPorSaldoOrigen) {
      if (necesitaSeleccionItemCartera) setMonto('');
      return;
    }

    cargarMontoSugeridoOrigen();
  }, [
    mostrarFormularioNueva,
    chequeraOrigen?.mcp,
    grupoSeleccionado?.codigo,
    sugiereMontoPorSaldoOrigen,
    necesitaSeleccionItemCartera,
    cargarMontoSugeridoOrigen,
  ]);

  const ordenarCheques = useCallback((cheques: ChequeCartera[]): ChequeCartera[] => {
    if (!orderByCheque) return cheques;
    return [...cheques].sort((a, b) => {
      let aValue: any = a[orderByCheque];
      let bValue: any = b[orderByCheque];
      if (aValue == null) aValue = '';
      if (bValue == null) bValue = '';
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return orderCheque === 'asc' ? aValue - bValue : bValue - aValue;
      }
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      return orderCheque === 'asc'
        ? (aStr < bStr ? -1 : aStr > bStr ? 1 : 0)
        : (aStr > bStr ? -1 : aStr < bStr ? 1 : 0);
    });
  }, [orderByCheque, orderCheque]);

  const ordenarChequesParaListado = useCallback(
    (cheques: ChequeCartera[]): ChequeCartera[] => {
      if (cheques.length === 0) return cheques;
      const tienePrioridad =
        chequesFijosDesdeCartera.size > 0 || chequesCarteraSeleccionados.size > 0;
      if (!tienePrioridad) return ordenarCheques(cheques);

      const prioridad = new Set([
        ...Array.from(chequesCarteraSeleccionados),
        ...Array.from(chequesFijosDesdeCartera),
      ]);
      const seleccionados: ChequeCartera[] = [];
      const restantes: ChequeCartera[] = [];
      for (const c of cheques) {
        if (prioridad.has(c.nroCarga)) seleccionados.push(c);
        else restantes.push(c);
      }
      return [...ordenarCheques(seleccionados), ...ordenarCheques(restantes)];
    },
    [ordenarCheques, chequesCarteraSeleccionados, chequesFijosDesdeCartera]
  );

  const handleRequestSortCheque = (property: keyof ChequeCartera) => {
    const isAsc = orderByCheque === property && orderCheque === 'asc';
    setOrderCheque(isAsc ? 'desc' : 'asc');
    setOrderByCheque(property);
  };

  const handleSeleccionarTodosCheques = (chequesFiltrados: ChequeCartera[]) => {
    if (seleccionChequesBloqueadaDesdeCartera) return;
    const todosSeleccionados = chequesFiltrados.every((c) => chequesCarteraSeleccionados.has(c.nroCarga));
    startTransition(() => {
      const nuevoSet = new Set(chequesCarteraSeleccionados);
      if (todosSeleccionados) {
        chequesFiltrados.forEach((c) => nuevoSet.delete(c.nroCarga));
      } else {
        chequesFiltrados.forEach((c) => nuevoSet.add(c.nroCarga));
      }
      setChequesCarteraSeleccionados(nuevoSet);
    });
  };

  const nroCargasChequesPendientes = useMemo(() => {
    const set = new Set<number>();
    transferenciasPendientes.forEach((t) => {
      const nro = extraerNroCargaChequePendiente(t);
      if (nro != null) set.add(nro);
    });
    return set;
  }, [transferenciasPendientes]);

  const nroCargasCuponesPendientes = useMemo(() => {
    const set = new Set<number>();
    transferenciasPendientes.forEach((t) => {
      const nro = extraerNroCargaCuponPendiente(t);
      if (nro != null) set.add(nro);
    });
    return set;
  }, [transferenciasPendientes]);

  /** API + ítems que esta sesión ingresó al grupo origen actual (sin esperar refetch ni salir de la pantalla). */
  const chequesCarteraConSesion = useMemo(() => {
    const grupo = grupoSeleccionado?.codigo;
    if (grupo == null) return chequesCartera;
    const ingresados = ingresosCarteraPorGrupo[grupo]?.cheques ?? [];
    if (ingresados.length === 0) return chequesCartera;
    const mcpNorm = (chequeraOrigen?.mcp ?? '').trim().toUpperCase();
    // Mostrar ingresos del grupo; alinear mcp al medio origen elegido para que coincida el filtro de cartera.
    const extras = ingresados.map((c) =>
      mcpNorm ? { ...c, mcp: chequeraOrigen?.mcp ?? c.mcp } : c
    );
    return mergeChequesPorNroCarga(chequesCartera, extras);
  }, [chequesCartera, ingresosCarteraPorGrupo, grupoSeleccionado?.codigo, chequeraOrigen?.mcp]);

  const cuponesCarteraConSesion = useMemo(() => {
    const grupo = grupoSeleccionado?.codigo;
    if (grupo == null) return cuponesCartera;
    const ingresados = ingresosCarteraPorGrupo[grupo]?.cupones ?? [];
    if (ingresados.length === 0) return cuponesCartera;
    const mcpNorm = (chequeraOrigen?.mcp ?? '').trim().toUpperCase();
    const extras = ingresados.map((c) =>
      mcpNorm ? { ...c, mcp: chequeraOrigen?.mcp ?? c.mcp } : c
    );
    return mergeCuponesPorNroCarga(cuponesCartera, extras);
  }, [cuponesCartera, ingresosCarteraPorGrupo, grupoSeleccionado?.codigo, chequeraOrigen?.mcp]);

  const chequesCarteraDisponibles = useMemo(
    () =>
      chequesCarteraConSesion.filter((c) => {
        const nro = nroCargaCuponValido(c.nroCarga);
        if (nro == null) return false;
        if (nroCargasChequesPendientes.has(nro)) return false;
        const grupoOrigen = grupoSeleccionado?.codigo;
        if (
          grupoOrigen != null &&
          clavesChequesUsadosSesion.has(claveCarteraUsadaSesion(grupoOrigen, nro))
        ) {
          return false;
        }
        return true;
      }),
    [
      chequesCarteraConSesion,
      nroCargasChequesPendientes,
      clavesChequesUsadosSesion,
      grupoSeleccionado?.codigo,
    ]
  );

  const cuponesCarteraDisponibles = useMemo(
    () =>
      cuponesCarteraConSesion.filter((c) => {
        const nro = nroCargaCuponValido(c.nroCarga);
        if (nro == null) return false;
        if (nroCargasCuponesPendientes.has(nro)) return false;
        const grupoOrigen = grupoSeleccionado?.codigo;
        if (
          grupoOrigen != null &&
          clavesCuponesUsadosSesion.has(claveCarteraUsadaSesion(grupoOrigen, nro))
        ) {
          return false;
        }
        return true;
      }),
    [
      cuponesCarteraConSesion,
      nroCargasCuponesPendientes,
      clavesCuponesUsadosSesion,
      grupoSeleccionado?.codigo,
    ]
  );

  const chequesSeleccionadosLista = useMemo(() => {
    return chequesCarteraDisponibles.filter((c) => {
      const nro = nroCargaCuponValido(c.nroCarga);
      return nro != null && chequesCarteraSeleccionados.has(nro);
    });
  }, [chequesCarteraDisponibles, chequesCarteraSeleccionados]);

  const chequesFiltradosPorBusqueda = useMemo(
    () => filtrarChequesCarteraPorBusqueda(chequesCarteraDisponibles, busquedaCheque),
    [chequesCarteraDisponibles, busquedaCheque]
  );

  const chequesOrdenadosListado = useMemo(
    () => ordenarChequesParaListado(chequesFiltradosPorBusqueda),
    [chequesFiltradosPorBusqueda, ordenarChequesParaListado]
  );

  const chequesOrdenadosPagina = useMemo(() => {
    const start = chequesTablaPage * chequesTablaRowsPerPage;
    return chequesOrdenadosListado.slice(start, start + chequesTablaRowsPerPage);
  }, [chequesOrdenadosListado, chequesTablaPage, chequesTablaRowsPerPage]);

  const todosChequesFiltradosSeleccionados = useMemo(
    () =>
      chequesOrdenadosListado.length > 0 &&
      chequesOrdenadosListado.every((c) => chequesCarteraSeleccionados.has(c.nroCarga)),
    [chequesOrdenadosListado, chequesCarteraSeleccionados]
  );

  useEffect(() => {
    setChequesTablaPage(0);
  }, [busquedaCheque, chequesCarteraDisponibles.length, getChequesCarteraCacheKey]);

  useEffect(() => {
    planillaDestinoCacheRef.current.clear();
  }, [grupoDestinoSeleccionado?.codigo]);

  const verificarPlanillaDestino = useCallback(async (grupoDestino: number): Promise<boolean> => {
    const cached = planillaDestinoCacheRef.current.get(grupoDestino);
    if (cached !== undefined) return cached;
    try {
      const ok = await cajaAbiertaEnGrupo(grupoDestino);
      planillaDestinoCacheRef.current.set(grupoDestino, ok);
      return ok;
    } catch {
      return false;
    }
  }, []);

  const cuponesSeleccionadosLista = useMemo(() => {
    return cuponesCarteraDisponibles.filter((c) => {
      const nro = nroCargaCuponValido(c.nroCarga);
      return nro != null && cuponesCarteraSeleccionados.has(nro);
    });
  }, [cuponesCarteraDisponibles, cuponesCarteraSeleccionados]);

  const ordenarCupones = useCallback((cupones: CuponCartera[]): CuponCartera[] => {
    if (!orderByCupon) return cupones;
    return [...cupones].sort((a, b) => {
      let aValue: any = a[orderByCupon];
      let bValue: any = b[orderByCupon];
      if (aValue == null) aValue = '';
      if (bValue == null) bValue = '';
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return orderCupon === 'asc' ? aValue - bValue : bValue - aValue;
      }
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      return orderCupon === 'asc'
        ? (aStr < bStr ? -1 : aStr > bStr ? 1 : 0)
        : (aStr > bStr ? -1 : aStr < bStr ? 1 : 0);
    });
  }, [orderByCupon, orderCupon]);

  const handleRequestSortCupon = (property: keyof CuponCartera) => {
    const isAsc = orderByCupon === property && orderCupon === 'asc';
    setOrderCupon(isAsc ? 'desc' : 'asc');
    setOrderByCupon(property);
  };

  const handleSeleccionarTodosCupones = (cuponesFiltrados: CuponCartera[]) => {
    const idsValidos = cuponesFiltrados
      .map((c) => nroCargaCuponValido(c.nroCarga))
      .filter((nro): nro is number => nro != null);
    const todosSeleccionados =
      idsValidos.length > 0 && idsValidos.every((nro) => cuponesCarteraSeleccionados.has(nro));
    const nuevoSet = new Set(cuponesCarteraSeleccionados);
    if (todosSeleccionados) {
      idsValidos.forEach((nro) => nuevoSet.delete(nro));
    } else {
      idsValidos.forEach((nro) => nuevoSet.add(nro));
    }
    setCuponesCarteraSeleccionados(nuevoSet);
  };

  const buildTarjetasCarteraQuery = useCallback((): TarjetasCarteraQueryParams => {
    const q: TarjetasCarteraQueryParams = {};
    const mcpOrigen = (chequeraOrigen?.mcp ?? '').trim();
    if (mcpOrigen) q.mcp = mcpOrigen;
    return q;
  }, [chequeraOrigen?.mcp]);

  useEffect(() => {
    if (!mostrarSeleccionCheques) {
      setSucursalesBancoCheque([]);
      return;
    }
    const loadAux = async () => {
      try {
        const token = getAccessToken();
        const headers = { Authorization: token ? `Bearer ${token}` : undefined };
        const sucRes = await api.get('/api/Tesoreria/sucursales-banco', { headers });
        setSucursalesBancoCheque(Array.isArray(sucRes.data) ? sucRes.data : []);
      } catch {
        setSucursalesBancoCheque([]);
      }
    };
    loadAux();
  }, [mostrarSeleccionCheques]);

  // Cargar cheques de cartera cuando origen/destino están listos (todos los registros, paginado)
  useEffect(() => {
    if (!mostrarFormularioNueva || !grupoSeleccionado?.codigo || !mostrarSeleccionCheques) {
      resetChequesCartera();
      return;
    }
    const mcpOrigen = (chequeraOrigen?.mcp ?? '').trim();
    if (!mcpOrigen) {
      resetChequesCartera();
      return;
    }
    if (!grupoDestinoSeleccionado?.codigo || !chequeraDestino?.mcp) {
      // Evitar mostrar cartera de otro origen/MCP mientras se elige destino.
      resetChequesCartera();
      return;
    }

    const cacheKey = getChequesCarteraCacheKey();
    if (!cacheKey) return;
    if (cacheKey === chequesCarteraCacheKeyRef.current && chequesCarteraCargadosRef.current) {
      return;
    }

    let cancelled = false;

    const cargarCheques = async () => {
      setLoadingChequesCartera(true);
      setCargaChequesProgress({ message: 'Cargando cheques de cartera...', currentPage: 0, totalPages: 0 });
      try {
        const caja = await tesoreriaService.getCajaAbiertaPorGrupo(grupoSeleccionado.codigo);
        const codigoCaja = (caja as any)?.codigoCaja ?? (caja as any)?.nroCaja ?? (caja as any)?.codigo;
        if (!codigoCaja) {
          resetChequesCartera();
          return;
        }
        const filtros = buildChequesCarteraQuery();
        const todos = await tesoreriaService.getChequesCarteraAll(codigoCaja, filtros, (current, total) => {
          if (cancelled) return;
          setCargaChequesProgress({
            message:
              total > 1
                ? `Cargando cheques de cartera (${current} de ${total} páginas)...`
                : 'Cargando cheques de cartera...',
            currentPage: current,
            totalPages: total,
          });
        });
        if (cancelled) return;
        const chequesConIdentidad = todos.filter((c) => nroCargaCuponValido(c.nroCarga) != null);
        const chequesList = chequesConIdentidad.length > 0 ? chequesConIdentidad : todos;
        chequesCarteraCacheKeyRef.current = cacheKey;
        chequesCarteraCargadosRef.current = true;
        setChequesCartera(chequesList);
        const fijos = chequesFijosDesdeCarteraRef.current;
        if (fijos.size > 0) {
          setChequesCarteraSeleccionados(new Set(fijos));
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Error al cargar cheques de cartera para traspasos:', error);
        enqueueSnackbar('Error al cargar cheques de cartera para la caja origen.', { variant: 'error' });
        resetChequesCartera();
      } finally {
        if (!cancelled) {
          setLoadingChequesCartera(false);
          setCargaChequesProgress({ message: '', currentPage: 0, totalPages: 0 });
        }
      }
    };

    cargarCheques();

    return () => {
      cancelled = true;
    };
  }, [
    mostrarFormularioNueva,
    grupoSeleccionado?.codigo,
    grupoDestinoSeleccionado?.codigo,
    chequeraDestino?.mcp,
    mostrarSeleccionCheques,
    chequeraOrigen?.mcp,
    carteraReloadToken,
    enqueueSnackbar,
    getChequesCarteraCacheKey,
    buildChequesCarteraQuery,
    resetChequesCartera,
  ]);

  // Recalcular cupones disponibles al abrir el formulario (con grupo), sin esperar a elegir tarjeta.
  useEffect(() => {
    if (!mostrarFormularioNueva || !grupoSeleccionado?.codigo) {
      setCuponesCartera([]);
      setCuponesCarteraSeleccionados(new Set());
      return;
    }

    let cancelled = false;

    const cargarCupones = async () => {
      setLoadingCuponesCartera(true);
      try {
        const caja = await tesoreriaService.getCajaAbiertaPorGrupo(grupoSeleccionado.codigo);
        const codigoCaja = (caja as any)?.codigoCaja ?? (caja as any)?.nroCaja ?? (caja as any)?.codigo;
        if (!codigoCaja) {
          if (!cancelled) setCuponesCartera([]);
          return;
        }
        const filtros = buildTarjetasCarteraQuery();
        const todos = await tesoreriaService.getTarjetasCarteraAll(codigoCaja, filtros);
        if (cancelled) return;
        const cuponesConIdentidad = todos.filter((c) => nroCargaCuponValido(c.nroCarga) != null);
        setCuponesCartera(cuponesConIdentidad.length > 0 ? cuponesConIdentidad : todos);
      } catch (error) {
        if (cancelled) return;
        console.error('Error al cargar cupones de cartera para traspasos:', error);
        enqueueSnackbar('Error al cargar cupones de tarjeta en cartera para la caja origen.', { variant: 'error' });
        setCuponesCartera([]);
      } finally {
        if (!cancelled) setLoadingCuponesCartera(false);
      }
    };

    cargarCupones();
    return () => {
      cancelled = true;
    };
  }, [
    mostrarFormularioNueva,
    grupoSeleccionado?.codigo,
    chequeraOrigen?.mcp,
    carteraReloadToken,
    enqueueSnackbar,
    buildTarjetasCarteraQuery,
  ]);

  const fetchTransferencias = useCallback(
    async (
      pageNumber: number,
      pageSize: number,
      search: string,
      desde: string,
      hasta: string,
      nroSuc: number | null
    ) => {
      setLoadingListado(true);
      try {
        const result = await tesoreriaService.getTransferenciasCajaPaged({
          pageNumber: pageNumber + 1,
          pageSize,
          searchTerm: search || undefined,
          fechaDesde: desde || undefined,
          fechaHasta: hasta || undefined,
          nroSuc: resolveNroSucFiltro(nroSuc),
        });
        setTransferencias(result.items);
        setTotalCount(result.totalCount);
      } catch (error) {
        console.error('Error al cargar traspasos:', error);
        enqueueSnackbar('Error al cargar el listado de traspasos', { variant: 'error' });
        setTransferencias([]);
        setTotalCount(0);
      } finally {
        setLoadingListado(false);
      }
    },
    [enqueueSnackbar]
  );

  usePersistTesoreriaFiltros(
    TESORERIA_FILTROS_KEYS.transferencias,
    { searchTerm, fechaDesde, fechaHasta, rowsPerPage },
    [searchTerm, fechaDesde, fechaHasta, rowsPerPage]
  );

  useEffect(() => {
    if (!mostrarFormularioNueva && filtroListo) {
      fetchTransferencias(page, rowsPerPage, searchTerm, fechaDesde, fechaHasta, filtroNroSuc);
    }
  }, [page, rowsPerPage, searchTerm, fechaDesde, fechaHasta, filtroNroSuc, filtroListo, mostrarFormularioNueva, fetchTransferencias]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const limpiarLineaTraspaso = () => {
    setDetalle('');
    setObservaciones('');
    setMonto('');
    setChequesCarteraSeleccionados(new Set());
    setChequesFijosDesdeCartera(new Set());
    setBusquedaCheque('');
    setCuponesCarteraSeleccionados(new Set());
    setBusquedaCupon('');
  };

  const handleAgregarTransferencia = async () => {
    if (!grupoSeleccionado?.codigo) {
      enqueueSnackbar('Seleccione el Grupo de Caja', { variant: 'warning' });
      return;
    }
    if (!chequeraOrigen?.mcp) {
      enqueueSnackbar('Seleccione la Chequera de Origen', { variant: 'warning' });
      return;
    }
    if (esMedioChequePropio(chequeraOrigen) || esMedioChequePropio(chequeraDestino)) {
      enqueueSnackbar(MSG_CHEQUE_PROPIO_NO_TRASPASABLE, { variant: 'warning' });
      return;
    }
    if (!chequeraDestino?.mcp) {
      enqueueSnackbar('Seleccione la Chequera de Destino', { variant: 'warning' });
      return;
    }

    if (!grupoDestinoSeleccionado?.codigo) {
      enqueueSnackbar(MSG_SIN_GRUPO_DESTINO, { variant: 'warning' });
      return;
    }

    if (mostrarSeleccionCheques) {
      if (chequesCarteraSeleccionados.size === 0) {
        enqueueSnackbar('Debe seleccionar al menos un cheque del listado de cheques en cartera de la caja.', {
          variant: 'warning',
        });
        return;
      }
      const sinNroCarga = chequesSeleccionadosLista.filter(
        (c) => nroCargaCuponValido(c.nroCarga) == null
      );
      if (sinNroCarga.length > 0) {
        enqueueSnackbar(
          'Uno o más cheques seleccionados no tienen número de carga válido y no se pueden traspasar.',
          { variant: 'warning' }
        );
        return;
      }
      const duplicados = chequesSeleccionadosLista.filter((c) => {
        const nro = nroCargaCuponValido(c.nroCarga);
        return nro != null && nroCargasChequesPendientes.has(nro);
      });
      if (duplicados.length > 0) {
        enqueueSnackbar('Uno o más cheques seleccionados ya están en el listado de traspasos.', {
          variant: 'warning',
        });
        return;
      }
      const yaUsados = chequesSeleccionadosLista.filter((c) => {
        const nro = nroCargaCuponValido(c.nroCarga);
        const grupoOrigen = grupoSeleccionado?.codigo;
        return (
          nro != null &&
          grupoOrigen != null &&
          clavesChequesUsadosSesion.has(claveCarteraUsadaSesion(grupoOrigen, nro))
        );
      });
      if (yaUsados.length > 0) {
        enqueueSnackbar('Uno o más cheques seleccionados ya fueron traspasados desde esta caja en esta sesión.', {
          variant: 'warning',
        });
        return;
      }
    }

    if (mostrarSeleccionCupones) {
      if (cuponesCarteraSeleccionados.size === 0) {
        enqueueSnackbar('Debe seleccionar al menos un cupón del listado de tarjetas en cartera de la caja.', {
          variant: 'warning',
        });
        return;
      }
      const sinNroCarga = cuponesSeleccionadosLista.filter(
        (c) => nroCargaCuponValido(c.nroCarga) == null
      );
      if (sinNroCarga.length > 0) {
        enqueueSnackbar(
          'Uno o más cupones seleccionados no tienen número de carga válido y no se pueden traspasar.',
          { variant: 'warning' }
        );
        return;
      }
      const duplicados = cuponesSeleccionadosLista.filter((c) => {
        const nro = nroCargaCuponValido(c.nroCarga);
        return nro != null && nroCargasCuponesPendientes.has(nro);
      });
      if (duplicados.length > 0) {
        enqueueSnackbar('Uno o más cupones seleccionados ya están en el listado de traspasos.', {
          variant: 'warning',
        });
        return;
      }
      const yaUsados = cuponesSeleccionadosLista.filter((c) => {
        const nro = nroCargaCuponValido(c.nroCarga);
        const grupoOrigen = grupoSeleccionado?.codigo;
        return (
          nro != null &&
          grupoOrigen != null &&
          clavesCuponesUsadosSesion.has(claveCarteraUsadaSesion(grupoOrigen, nro))
        );
      });
      if (yaUsados.length > 0) {
        enqueueSnackbar('Uno o más cupones seleccionados ya fueron traspasados desde esta caja en esta sesión.', {
          variant: 'warning',
        });
        return;
      }
    }

    const montoNum = mostrarSeleccionCheques && chequesSeleccionadosLista.length > 0
      ? chequesSeleccionadosLista.reduce((s, c) => s + (c.importe ?? 0), 0)
      : mostrarSeleccionCupones && cuponesSeleccionadosLista.length > 0
        ? cuponesSeleccionadosLista.reduce((s, c) => s + (c.montoOperacion ?? 0), 0)
        : parseFloat(String(monto).replace(',', '.'));
    if (isNaN(montoNum) || montoNum <= 0) {
      enqueueSnackbar(
        mostrarSeleccionCheques || mostrarSeleccionCupones
          ? 'Seleccione al menos un ítem de cartera.'
          : 'Ingrese un monto válido',
        { variant: 'warning' }
      );
      return;
    }

    const nroSucValue: number =
      (nroSucSeleccionado && nroSucSeleccionado > 0 ? nroSucSeleccionado : 0) ||
      Number(chequeraOrigen.nroSuc || chequeraDestino.nroSuc || 0) ||
      0;

    if (!nroSucValue || nroSucValue <= 0) {
      enqueueSnackbar('Debe seleccionar un N° de Sucursal válido.', {
        variant: 'error',
      });
      return;
    }

    if (!fechaEmisionForm || isNaN(new Date(`${fechaEmisionForm}T12:00:00`).getTime())) {
      enqueueSnackbar('Indique una fecha de emisión válida.', { variant: 'warning' });
      return;
    }

    const grupoDestino = grupoDestinoSeleccionado.codigo;
    if (!Number.isFinite(grupoDestino) || grupoDestino <= 0) {
      enqueueSnackbar(MSG_SIN_GRUPO_DESTINO, { variant: 'warning' });
      return;
    }
    if (
      chequeraDestino.grupo != null &&
      Number(chequeraDestino.grupo) !== Number(grupoDestino)
    ) {
      enqueueSnackbar('El medio de pago destino no pertenece al grupo de caja destino seleccionado.', {
        variant: 'warning',
      });
      return;
    }
    if (Number(grupoDestino) === Number(grupoSeleccionado.codigo)) {
      enqueueSnackbar(MSG_MISMA_CAJA_DESTINO, { variant: 'warning' });
      return;
    }
    try {
      const tienePlanillaAbierta = await verificarPlanillaDestino(Number(grupoDestino));
      if (!tienePlanillaAbierta) {
        enqueueSnackbar(MSG_SIN_PLANILLA_ABIERTA, { variant: 'warning' });
        return;
      }
    } catch {
      enqueueSnackbar('No se pudo verificar la planilla de caja del grupo destino.', { variant: 'error' });
      return;
    }

    const chequePayload = (ch: ChequeCartera) => {
      const nroCarga = nroCargaCuponValido(ch.nroCarga);
      return {
        nroBanco: ch.nroBanco ?? 0,
        nroSucursal: ch.nroSucursal ?? 0,
        nroCheque: formatNroCheque(ch.nroCheque),
        nroLibrador: ch.nroLibrador ?? '',
        librador: ch.librador ?? '',
        fechaEmision: ch.fechaEmision || new Date().toISOString(),
        fechaVencimiento: ch.fechaVencimiento || new Date().toISOString(),
        ctaBancaria: String(ch.nroCuenta ?? '').trim().slice(0, 30),
        concepto: (ch.concepto ?? '').trim().slice(0, 30),
        ...(nroCarga != null ? { nroCarga } : {}),
      };
    };

    const itemsChequesAAgregar =
      mostrarSeleccionCheques && chequesSeleccionadosLista.length > 0
        ? chequesSeleccionadosLista
        : [];
    const itemsCuponesAAgregar =
      mostrarSeleccionCupones && cuponesSeleccionadosLista.length > 0
        ? cuponesSeleccionadosLista
        : [];
    const itemsAAgregar: Array<{ tipo: 'cheque'; item: ChequeCartera } | { tipo: 'cupon'; item: CuponCartera } | { tipo: 'simple' }> =
      itemsChequesAAgregar.length > 0
        ? itemsChequesAAgregar.map((item) => ({ tipo: 'cheque' as const, item }))
        : itemsCuponesAAgregar.length > 0
          ? itemsCuponesAAgregar.map((item) => ({ tipo: 'cupon' as const, item }))
          : [{ tipo: 'simple' as const }];

    const nuevosPendientes: TransferenciaPendiente[] = [];

    for (const row of itemsAAgregar) {
      const ch = row.tipo === 'cheque' ? row.item : null;
      const cupon = row.tipo === 'cupon' ? row.item : null;
      const importeItem = ch
        ? (ch.importe ?? 0)
        : cupon
          ? (cupon.montoOperacion ?? 0)
          : montoNum;
      const moOrigen: any = {
        mcp: chequeraOrigen.mcp,
        importe: importeItem,
        detalle: detalle || '',
      };
      const moDestino: any = {
        mcp: chequeraDestino.mcp,
        importe: importeItem,
        detalle: detalle || '',
      };
      if (esOrigenChequesTercero && ch) {
        moOrigen.cheque = chequePayload(ch);
      }
      if (esCheque && ch) {
        moDestino.cheque = chequePayload(ch);
      }
      const cuponPayload = (c: CuponCartera) => {
        const nroCarga = nroCargaCuponValido(c.nroCarga);
        return { nroCarga: nroCarga as number };
      };
      if (esOrigenTarjeta && cupon) {
        moOrigen.tarjeta = cuponPayload(cupon);
      }
      if (esDestinoTarjeta && cupon) {
        moDestino.tarjeta = cuponPayload(cupon);
      }
      const fechaDoc = fechaEmisionForm
        ? new Date(`${fechaEmisionForm}T12:00:00`)
        : new Date();
      const payload: any = {
        fecha: fechaDoc.toISOString(),
        nroSuc: nroSucValue,
        descripcion: (observaciones.trim() || detalle.trim()) || '',
        aclaracion: '',
        medioCobroOrigen: moOrigen,
        medioCobroDestino: moDestino,
      };
      nuevosPendientes.push({
        payload,
        mcpOrigen: (chequeraOrigen.mcp ?? '').trim(),
        mcpDestino: (chequeraDestino.mcp ?? '').trim(),
        grupoDestinoNombre: grupoDestinoSeleccionado.nombre || `Grupo ${grupoDestino}`,
        detalle: detalle.trim() || '',
        observaciones: (observaciones.trim() || detalle.trim()) || '',
        importe: importeItem,
        grupoOrigen: Number(grupoSeleccionado.codigo),
        grupoDestino: Number(grupoDestino),
        ...(ch ? { chequeCartera: ch } : {}),
        ...(cupon ? { cuponCartera: cupon } : {}),
      });
    }

    setTransferenciasPendientes((prev) => [...prev, ...nuevosPendientes]);
    limpiarLineaTraspaso();
    enqueueSnackbar(
      nuevosPendientes.length > 1
        ? `${nuevosPendientes.length} traspasos agregados al listado`
        : 'Traspaso agregado al listado',
      { variant: 'success' }
    );
  };

  const handleQuitarPendiente = (idx: number) => {
    setTransferenciasPendientes((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleGuardarTodas = async () => {
    if (transferenciasPendientes.length === 0) {
      enqueueSnackbar('Agregue al menos un traspaso al listado', { variant: 'warning' });
      return;
    }
    const gruposUnicos = Array.from(new Set(transferenciasPendientes.map((t) => t.grupoDestino)));
    for (const grupo of gruposUnicos) {
      if (!Number.isFinite(grupo) || grupo <= 0) {
        enqueueSnackbar(MSG_SIN_GRUPO_DESTINO, { variant: 'error' });
        return;
      }
      try {
        const tienePlanillaAbierta = await cajaAbiertaEnGrupo(grupo);
        if (!tienePlanillaAbierta) {
          enqueueSnackbar(MSG_SIN_PLANILLA_ABIERTA, { variant: 'error' });
          return;
        }
      } catch {
        enqueueSnackbar('No se pudo verificar la planilla de caja del grupo destino.', { variant: 'error' });
        return;
      }
    }
    setGuardandoTodas(true);
    let ok = 0;
    let _err = 0;
    const clavesChequeGuardadas: string[] = [];
    const clavesCuponGuardadas: string[] = [];
    const guardadosOk: TransferenciaPendiente[] = [];
    for (const item of transferenciasPendientes) {
      try {
        await tesoreriaService.postTransferenciaCaja(item.payload);
        ok += 1;
        guardadosOk.push(item);
        const grupoOrigen = Number(item.grupoOrigen);
        const nroCheque = extraerNroCargaChequePendiente(item);
        if (nroCheque != null && Number.isFinite(grupoOrigen) && grupoOrigen > 0) {
          clavesChequeGuardadas.push(claveCarteraUsadaSesion(grupoOrigen, nroCheque));
        } else if (item.chequeCartera) {
          const nro = nroCargaCuponValido(item.chequeCartera.nroCarga);
          if (nro != null && Number.isFinite(grupoOrigen) && grupoOrigen > 0) {
            clavesChequeGuardadas.push(claveCarteraUsadaSesion(grupoOrigen, nro));
          }
        }
        const nroCupon = extraerNroCargaCuponPendiente(item);
        if (nroCupon != null && Number.isFinite(grupoOrigen) && grupoOrigen > 0) {
          clavesCuponGuardadas.push(claveCarteraUsadaSesion(grupoOrigen, nroCupon));
        } else if (item.cuponCartera) {
          const nro = nroCargaCuponValido(item.cuponCartera.nroCarga);
          if (nro != null && Number.isFinite(grupoOrigen) && grupoOrigen > 0) {
            clavesCuponGuardadas.push(claveCarteraUsadaSesion(grupoOrigen, nro));
          }
        }
      } catch (error: any) {
        _err += 1;
        console.error('❌ Error en postTransferenciaCaja:', error?.response?.data || error);
        const msg =
          error?.response?.data?.message ||
          error?.response?.data?.title ||
          error?.response?.data?.detail ||
          JSON.stringify(error?.response?.data) ||
          error?.message ||
          'Error';
        enqueueSnackbar(`Error en traspaso: ${msg}`, { variant: 'error' });
      }
    }
    setGuardandoTodas(false);
    if (ok > 0) {
      if (clavesChequeGuardadas.length > 0) {
        setClavesChequesUsadosSesion((prev) => {
          const next = new Set(prev);
          clavesChequeGuardadas.forEach((k) => next.add(k));
          return next;
        });
      }
      if (clavesCuponGuardadas.length > 0) {
        setClavesCuponesUsadosSesion((prev) => {
          const next = new Set(prev);
          clavesCuponGuardadas.forEach((k) => next.add(k));
          return next;
        });
      }
      setIngresosCarteraPorGrupo((prev) => {
        let next = prev;
        for (const item of guardadosOk) {
          const cheque = item.chequeCartera
            ? { ...item.chequeCartera, mcp: item.mcpDestino || item.chequeCartera.mcp }
            : undefined;
          const cupon = item.cuponCartera
            ? { ...item.cuponCartera, mcp: item.mcpDestino || item.cuponCartera.mcp }
            : undefined;
          if (!cheque && !cupon) continue;
          next = agregarIngresoCartera(
            next,
            Number(item.grupoDestino),
            Number(item.grupoOrigen),
            cheque,
            cupon
          );
        }
        return next;
      });
      enqueueSnackbar(`${ok} traspaso(s) guardado(s) correctamente`, { variant: 'success' });
      resetAltaTraspaso();
      setCarteraReloadToken((n) => n + 1);
      setMostrarFormularioNueva(false);
      setPage(0);
    }
  };

  const handleCancelar = () => {
    if (!confirmarDescartePendientes()) return;
    setOpenGrupoCajaDialog(false);
    resetAltaTraspaso();
    setMostrarFormularioNueva(false);
  };

  const handleImprimirPDF = async (t: TransferenciaItem) => {
    const id = t.nroFormulario ?? t.id;
    if (id == null) {
      enqueueSnackbar('No se puede imprimir: falta número de formulario', { variant: 'warning' });
      return;
    }
    try {
      const response = await api.get(`/api/TesoreriaCaja/transferencias-caja/${id}/pdf`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      setPdfUrl(url);
      setSelectedTransferenciaId(id);
    } catch (err: any) {
      enqueueSnackbar(err?.response?.status === 404 ? 'Traspaso no encontrado' : 'Error al generar el PDF', { variant: 'error' });
    }
  };

  const handleClosePDF = () => {
    if (pdfUrl) window.URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setSelectedTransferenciaId(null);
  };

  const getOrigenLabel = (t: TransferenciaItem) =>
    t.mcpOrigen ?? t.origen ?? t.medioCobroOrigen?.mcp ?? '-';
  const getDestinoLabel = (t: TransferenciaItem) =>
    t.mcpDestino ?? t.destino ?? t.medioCobroDestino?.mcp ?? '-';
  const getMonto = (t: TransferenciaItem) =>
    Number(
      t.monto ??
        t.importe ??
        t.importeTotal ??
        t.medioCobroDestino?.importe ??
        t.medioCobroOrigen?.importe ??
        0
    );

  const formatComprobante = (t: TransferenciaItem) => {
    const { letra, nroSuc, nroComprobante } = t;
    if (!letra || nroSuc === undefined || nroComprobante === undefined || nroSuc === null || nroComprobante === null) {
      return '-';
    }
    const formatPart = (value: number | string | undefined | null, length: number) => {
      if (value === null || value === undefined) return ''.padStart(length, '0');
      const numeric = Number(value);
      if (!Number.isNaN(numeric)) return numeric.toString().padStart(length, '0');
      return String(value).padStart(length, '0');
    };
    const sucursal = formatPart(nroSuc, 5);
    const comprobante = formatPart(nroComprobante, 8);
    const letraNormalizada = (letra || '').trim().toUpperCase();
    return letraNormalizada ? `${letraNormalizada}-${sucursal}-${comprobante}` : '-';
  };

  const totalMonto = transferencias.reduce((sum, t) => sum + getMonto(t), 0);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {mostrarFormularioNueva ? (
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={1.5} mb={3} flexWrap="wrap">
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={handleToggleFormularioNueva}
                size="small"
                sx={{ textTransform: 'none' }}
              >
                Traspasos
              </Button>
              <Typography
                variant="h4"
                component="h1"
                sx={{ fontWeight: 600, color: '#1976d2' }}
              >
                Nuevo Traspaso
              </Typography>
            </Stack>
          ) : (
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
                Traspasos de caja
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleToggleFormularioNueva}
                sx={{
                  borderRadius: 2,
                  px: { xs: 2, sm: 3 },
                  py: { xs: 1.5, sm: 1 },
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)',
                  },
                }}
              >
                Nuevo traspaso
              </Button>
            </Box>
          )}

          {!mostrarFormularioNueva ? (
            <>
              {/* Filtros - mismo estilo que Recibos */}
              <Box sx={{ mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      placeholder="Buscar por número, descripción, nombre"
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setPage(0);
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          bgcolor: '#fafafa',
                          '&:hover': { bgcolor: '#f5f5f5' },
                          '&.Mui-focused': { bgcolor: '#ffffff' }
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: '#666' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      fullWidth
                      label="Fecha Desde"
                      type="date"
                      value={fechaDesde}
                      onChange={(e) => {
                        setFechaDesde(e.target.value);
                        setPage(0);
                      }}
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          bgcolor: '#fafafa',
                          '&:hover': { bgcolor: '#f5f5f5' },
                          '&.Mui-focused': { bgcolor: '#ffffff' }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      fullWidth
                      label="Fecha Hasta"
                      type="date"
                      value={fechaHasta}
                      onChange={(e) => {
                        setFechaHasta(e.target.value);
                        setPage(0);
                      }}
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          bgcolor: '#fafafa',
                          '&:hover': { bgcolor: '#f5f5f5' },
                          '&.Mui-focused': { bgcolor: '#ffffff' }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <FiltroSucursalVentaSelect
                      value={filtroNroSuc}
                      onChange={(v) => { setFiltroNroSuc(v); setPage(0); }}
                      loading={loadingSucursalesVentaFiltro}
                      sucursales={sucursalesVentaFiltro}
                    />
                  </Grid>
                </Grid>
              </Box>

              <TableContainer
                component={Paper}
                sx={{
                  borderRadius: 2,
                  boxShadow: 'none',
                  border: '1px solid #e0e0e0',
                  mb: 2,
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Nro. Formulario</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                      <TableCell sx={{ fontWeight: 600, minWidth: 110 }}>Comprobante</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Medio Origen</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Medio Destino</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Importe</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Usuario</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }} width={120}>Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loadingListado ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                          Cargando...
                        </TableCell>
                      </TableRow>
                    ) : transferencias.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 4 }} color="text.secondary">
                          No hay traspasos registrados.
                        </TableCell>
                      </TableRow>
                    ) : (
                      transferencias.map((t) => (
                        <TableRow key={t.id ?? t.nroFormulario ?? `${getOrigenLabel(t)}-${getDestinoLabel(t)}-${getMonto(t)}`} hover sx={{ '&:hover': { bgcolor: '#f9f9f9' } }}>
                          <TableCell sx={{ fontWeight: 500 }}>{t.nroFormulario ?? '-'}</TableCell>
                          <TableCell>{t.fechaEmision ? new Date(t.fechaEmision).toLocaleDateString() : '-'}</TableCell>
                          <TableCell>{formatComprobante(t)}</TableCell>
                          <TableCell>{getOrigenLabel(t)}</TableCell>
                          <TableCell>{getDestinoLabel(t)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, color: '#1976d2' }}>{formatCurrency(getMonto(t))}</TableCell>
                          <TableCell>{t.usuario ?? '-'}</TableCell>
                          <TableCell align="center">
                            <Tooltip title="Imprimir PDF">
                              <IconButton
                                onClick={() => handleImprimirPDF(t)}
                                size="small"
                                sx={{
                                  bgcolor: '#2196f3',
                                  color: '#fff',
                                  borderRadius: 1.5,
                                  mr: 0.5,
                                  '&:hover': { bgcolor: '#1976d2' },
                                }}
                              >
                                <PrintIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25]}
                  labelRowsPerPage="Filas por página"
                  sx={{ borderTop: '1px solid #e0e0e0' }}
                />
              </TableContainer>
              <Typography variant="body1" fontWeight={600}>
                Total: {formatCurrency(totalMonto)}
              </Typography>
            </>
          ) : (
          <>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={8}>
          <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  {...searchableSelectAutocompleteProps}
                  options={mcpsOrigenOrdenados}
                  groupBy={(option) => getEtiquetaCategoriaMedio(option.tipo)}
                  renderGroup={renderGrupoMedioAutocomplete}
                  getOptionLabel={(option) => getMedioAutocompleteLabel(option)}
                  filterOptions={(options, { inputValue }) => {
                    if (!inputValue?.trim()) return options;
                    const term = inputValue.trim().toLowerCase();
                    return options.filter((opt) => textoBusquedaMedio(opt).includes(term));
                  }}
                  isOptionEqualToValue={(opt, val) => {
                    if (!opt || !val) return opt === val;
                    return (
                      (opt.mcp ?? '').trim() === (val.mcp ?? '').trim() &&
                      (opt.tipo ?? '').trim().toUpperCase() === (val.tipo ?? '').trim().toUpperCase()
                    );
                  }}
                  value={chequeraOrigen}
                  onOpen={() => setOrigenAutocompleteAbierto(true)}
                  onClose={() => setOrigenAutocompleteAbierto(false)}
                  onChange={(_, newValue) => {
                    setChequeraOrigen(newValue);
                    setChequeraDestino(null);
                    setChequesCarteraSeleccionados(new Set());
                    setChequesFijosDesdeCartera(new Set());
                    setBusquedaCheque('');
                    setCuponesCarteraSeleccionados(new Set());
                    setBusquedaCupon('');
                    if (!esTipoMcpConSaldoSugerido(newValue?.tipo)) {
                      setMonto('');
                    }
                  }}
                  loading={loadingMcps}
                  disabled={!grupoSeleccionado}
                  renderInput={(params) =>
                    renderMedioAutocompleteInput(
                      params,
                      'Medio Origen',
                      chequeraOrigen,
                      origenAutocompleteAbierto
                    )
                  }
                  renderOption={(props, option) =>
                    renderOpcionMedioAutocomplete(props, option, 'orig')
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  {...searchableSelectAutocompleteProps}
                  options={opcionesDestinoOrdenadas}
                  groupBy={(option) => getEtiquetaCategoriaMedio(option.tipo)}
                  renderGroup={renderGrupoMedioAutocomplete}
                  getOptionLabel={(option) => getMedioAutocompleteLabel(option)}
                  filterOptions={(options, { inputValue }) => {
                    if (!inputValue?.trim()) return options;
                    const term = inputValue.trim().toLowerCase();
                    return options.filter((opt) => textoBusquedaMedio(opt).includes(term));
                  }}
                  isOptionEqualToValue={(opt, val) => {
                    if (!opt || !val) return opt === val;
                    return (
                      (opt.mcp ?? '').trim() === (val.mcp ?? '').trim() &&
                      (opt.tipo ?? '').trim().toUpperCase() === (val.tipo ?? '').trim().toUpperCase()
                    );
                  }}
                  value={chequeraDestino}
                  onOpen={() => setDestinoAutocompleteAbierto(true)}
                  onClose={() => setDestinoAutocompleteAbierto(false)}
                  onChange={(_, newValue) => setChequeraDestino(newValue)}
                  loading={loadingMcpsDestino}
                  disabled={!chequeraOrigen || !grupoDestinoSeleccionado}
                  renderInput={(params) =>
                    renderMedioAutocompleteInput(
                      params,
                      'Medio Destino',
                      chequeraDestino,
                      destinoAutocompleteAbierto
                    )
                  }
                  renderOption={(props, option) =>
                    renderOpcionMedioAutocomplete(props, option, 'dest')
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Comentario"
                  value={detalle}
                  onChange={(e) => setDetalle(e.target.value)}
                  placeholder="Opcional"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Monto"
                  value={
                    mostrarSeleccionCheques && chequesSeleccionadosLista.length > 0
                      ? String(
                          chequesSeleccionadosLista.reduce((s, c) => s + (c.importe ?? 0), 0)
                        )
                      : mostrarSeleccionCupones && cuponesSeleccionadosLista.length > 0
                        ? String(
                            cuponesSeleccionadosLista.reduce((s, c) => s + (c.montoOperacion ?? 0), 0)
                          )
                        : monto
                  }
                  onChange={(e) => {
                    if (necesitaSeleccionItemCartera && (chequesCarteraSeleccionados.size > 0 || cuponesCarteraSeleccionados.size > 0)) return;
                    const v = e.target.value.replace(/[^0-9,.]/g, '').replace(',', '.');
                    if (v === '' || /^\d*\.?\d*$/.test(v)) setMonto(v === '' ? '' : v);
                  }}
                  placeholder="0.00"
                  inputProps={{ inputMode: 'decimal' }}
                  helperText={
                    sugiereMontoPorSaldoOrigen && monto
                      ? 'Monto sugerido según saldo del MCP origen'
                      : undefined
                  }
                  disabled={
                    necesitaSeleccionItemCartera &&
                    (chequesCarteraSeleccionados.size > 0 || cuponesCarteraSeleccionados.size > 0)
                  }
                />
              </Grid>
              <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleAgregarTransferencia}
                  fullWidth
                  sx={{ textTransform: 'none' }}
                >
                  Agregar
                </Button>
              </Grid>
            </Grid>

            {chequeraDestino && (necesitaSeleccionItemCartera || !esEfectivo) && (
              <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                {mostrarSeleccionCheques && (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Typography variant="subtitle2" color="text.secondary">
                            Seleccione uno o varios cheques de cartera:
                          </Typography>
                          {seleccionChequesBloqueadaDesdeCartera && (
                            <Chip
                              size="small"
                              color="info"
                              variant="outlined"
                              label={`${chequesFijosDesdeCartera.size} cheque${chequesFijosDesdeCartera.size > 1 ? 's' : ''} fijado${chequesFijosDesdeCartera.size > 1 ? 's' : ''} desde cartera`}
                            />
                          )}
                        </Stack>
                        {chequesCarteraDisponibles.length > 0 && !seleccionChequesBloqueadaDesdeCartera && (
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleSeleccionarTodosCheques(chequesOrdenadosListado)}
                              sx={{ minWidth: '140px' }}
                            >
                              {todosChequesFiltradosSeleccionados ? 'Deseleccionar todos' : 'Seleccionar todos'}
                            </Button>
                        )}
                      </Box>
                      <Accordion
                        variant="outlined"
                        sx={{ mb: 2 }}
                        disabled={seleccionChequesBloqueadaDesdeCartera}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <FilterAltIcon color="primary" fontSize="small" />
                            <Typography variant="subtitle2" fontWeight={600}>
                              Filtros avanzados (sobre MCP de chequera origen)
                            </Typography>
                          </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Grid container spacing={1.5}>
                            <Grid item xs={12}>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                Los cheques se listan solo para el MCP de la <strong>chequera origen</strong>
                                {chequeraOrigen?.mcp
                                  ? `: ${String(chequeraOrigen.mcp).trim()}`
                                  : ' (seleccione primero la chequera origen).'}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Nº banco"
                                type="number"
                                value={filtroChequeNroBanco}
                                onChange={(e) => {
                                  setFiltroChequeNroBanco(e.target.value);
                                  setFiltroChequeNroSucursal('');
                                }}
                                inputProps={{ min: 0 }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <SearchableSelect
                                label="Suc. banco"
                                size="small"
                                allowEmpty
                                emptyOptionLabel="Todas"
                                disabled={!filtroChequeNroBanco || sucursalesChequeFiltradas.length === 0}
                                options={sucursalesChequeFiltradas.map((s) => ({
                                  value: String(s.nroSucursal),
                                  label: `${s.sucursal} (${s.nroSucursal})`,
                                }))}
                                value={filtroChequeNroSucursal || null}
                                onChange={(value) => setFiltroChequeNroSucursal(value ?? '')}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                fullWidth
                                size="small"
                                type="date"
                                label="Ingreso desde"
                                value={filtroChequeFechaIngresoDesde}
                                onChange={(e) => setFiltroChequeFechaIngresoDesde(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                fullWidth
                                size="small"
                                type="date"
                                label="Ingreso hasta"
                                value={filtroChequeFechaIngresoHasta}
                                onChange={(e) => setFiltroChequeFechaIngresoHasta(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                fullWidth
                                size="small"
                                type="date"
                                label="Venc. desde"
                                value={filtroChequeFechaVencDesde}
                                onChange={(e) => setFiltroChequeFechaVencDesde(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                fullWidth
                                size="small"
                                type="date"
                                label="Venc. hasta"
                                value={filtroChequeFechaVencHasta}
                                onChange={(e) => setFiltroChequeFechaVencHasta(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Monto desde"
                                value={filtroChequeImporteDesde}
                                onChange={(e) => setFiltroChequeImporteDesde(e.target.value)}
                                inputProps={{ min: 0, step: 0.01 }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Monto hasta"
                                value={filtroChequeImporteHasta}
                                onChange={(e) => setFiltroChequeImporteHasta(e.target.value)}
                                inputProps={{ min: 0, step: 0.01 }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <SearchableSelect
                                label="Estado"
                                size="small"
                                allowEmpty
                                emptyOptionLabel="Todos"
                                options={[...ESTADO_CHEQUE_OPTIONS]}
                                value={filtroChequeEstado || null}
                                onChange={(value) => setFiltroChequeEstado(value ?? '')}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Librador / CUIT"
                                value={filtroChequeLibrador}
                                onChange={(e) => setFiltroChequeLibrador(e.target.value)}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Button variant="outlined" size="small" onClick={limpiarFiltrosAvanzadosCheques}>
                                Limpiar filtros avanzados
                              </Button>
                            </Grid>
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                      <TextField
                        fullWidth
                        placeholder="Buscar en resultados (banco, nº cheque, librador, cuenta, monto...)"
                        value={busquedaCheque}
                        onChange={(e) => setBusquedaCheque(e.target.value)}
                        size="small"
                        disabled={seleccionChequesBloqueadaDesdeCartera}
                        sx={{ mb: 2 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                      {loadingChequesCartera ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                          <Typography>Cargando cheques de cartera...</Typography>
                        </Box>
                      ) : chequesCarteraDisponibles.length === 0 ? (
                        <Box sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            {chequesCarteraConSesion.length === 0
                              ? 'No hay cheques disponibles en cartera.'
                              : 'Todos los cheques en cartera ya fueron agregados al listado de traspasos.'}
                          </Typography>
                        </Box>
                      ) : (
                        <>
                          <TableContainer component={Paper} sx={{ maxHeight: 400, overflowY: 'auto' }}>
                            <Table size="small" stickyHeader>
                              <TableHead>
                                <TableRow>
                                  <TableCell padding="checkbox" sx={{ width: 50 }}>
                                    <Checkbox
                                      indeterminate={
                                        chequesCarteraSeleccionados.size > 0 &&
                                        !todosChequesFiltradosSeleccionados
                                      }
                                      checked={
                                        todosChequesFiltradosSeleccionados &&
                                        chequesOrdenadosListado.length > 0
                                      }
                                      onChange={() =>
                                        handleSeleccionarTodosCheques(chequesOrdenadosListado)
                                      }
                                      disabled={seleccionChequesBloqueadaDesdeCartera}
                                      inputProps={{ 'aria-label': 'seleccionar todos los cheques' }}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>
                                    <TableSortLabel
                                      active={orderByCheque === 'nombreBanco'}
                                      direction={orderByCheque === 'nombreBanco' ? orderCheque : 'asc'}
                                      onClick={() => handleRequestSortCheque('nombreBanco')}
                                    >
                                      Banco
                                    </TableSortLabel>
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>
                                    <TableSortLabel
                                      active={orderByCheque === 'nroCheque'}
                                      direction={orderByCheque === 'nroCheque' ? orderCheque : 'asc'}
                                      onClick={() => handleRequestSortCheque('nroCheque')}
                                    >
                                      N° Cheque
                                    </TableSortLabel>
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>
                                    <TableSortLabel
                                      active={orderByCheque === 'nombreSucursal'}
                                      direction={orderByCheque === 'nombreSucursal' ? orderCheque : 'asc'}
                                      onClick={() => handleRequestSortCheque('nombreSucursal')}
                                    >
                                      Sucursal
                                    </TableSortLabel>
                                  </TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                                    <TableSortLabel
                                      active={orderByCheque === 'importe'}
                                      direction={orderByCheque === 'importe' ? orderCheque : 'asc'}
                                      onClick={() => handleRequestSortCheque('importe')}
                                    >
                                      Monto
                                    </TableSortLabel>
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>
                                    <TableSortLabel
                                      active={orderByCheque === 'librador'}
                                      direction={orderByCheque === 'librador' ? orderCheque : 'asc'}
                                      onClick={() => handleRequestSortCheque('librador')}
                                    >
                                      Librador
                                    </TableSortLabel>
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>
                                    <TableSortLabel
                                      active={orderByCheque === 'fechaEmision'}
                                      direction={orderByCheque === 'fechaEmision' ? orderCheque : 'asc'}
                                      onClick={() => handleRequestSortCheque('fechaEmision')}
                                    >
                                      Emisión
                                    </TableSortLabel>
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>
                                    <TableSortLabel
                                      active={orderByCheque === 'fechaVencimiento'}
                                      direction={orderByCheque === 'fechaVencimiento' ? orderCheque : 'asc'}
                                      onClick={() => handleRequestSortCheque('fechaVencimiento')}
                                    >
                                      Vencimiento
                                    </TableSortLabel>
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {chequesOrdenadosListado.length === 0 ? (
                                  <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                                      <Typography variant="body2" color="text.secondary">
                                        No se encontraron cheques que coincidan con la búsqueda
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                ) : (
                                  chequesOrdenadosPagina.map((cheque) => {
                                    const estaSeleccionado = chequesCarteraSeleccionados.has(
                                      cheque.nroCarga
                                    );
                                    const esFijoDesdeCartera = chequesFijosDesdeCartera.has(
                                      cheque.nroCarga
                                    );
                                    const seleccionDeshabilitada =
                                      seleccionChequesBloqueadaDesdeCartera &&
                                      (esFijoDesdeCartera || !estaSeleccionado);
                                    return (
                                      <FilaChequeCarteraTraspaso
                                        key={cheque.nroCarga}
                                        cheque={cheque}
                                        estaSeleccionado={estaSeleccionado}
                                        esFijoDesdeCartera={esFijoDesdeCartera}
                                        seleccionDeshabilitada={seleccionDeshabilitada}
                                        onToggle={actualizarSeleccionCheque}
                                      />
                                    );
                                  })
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          {chequesOrdenadosListado.length > chequesTablaRowsPerPage && (
                            <TablePagination
                              component="div"
                              count={chequesOrdenadosListado.length}
                              page={chequesTablaPage}
                              onPageChange={(_, newPage) => setChequesTablaPage(newPage)}
                              rowsPerPage={chequesTablaRowsPerPage}
                              onRowsPerPageChange={(e) => {
                                setChequesTablaRowsPerPage(parseInt(e.target.value, 10));
                                setChequesTablaPage(0);
                              }}
                              rowsPerPageOptions={[25, 50, 100]}
                              labelRowsPerPage="Filas"
                              sx={{ borderTop: 1, borderColor: 'divider' }}
                            />
                          )}
                          {chequesCarteraSeleccionados.size > 0 && (
                            <Typography variant="body2" color="primary" sx={{ mt: 1, fontWeight: 600 }}>
                              {chequesCarteraSeleccionados.size} cheque
                              {chequesCarteraSeleccionados.size > 1 ? 's' : ''} seleccionado
                              {chequesCarteraSeleccionados.size > 1 ? 's' : ''} · Total:{' '}
                              {formatCurrency(
                                chequesSeleccionadosLista.reduce((s, c) => s + (c.importe ?? 0), 0)
                              )}
                            </Typography>
                          )}
                        </>
                      )}
                    </Grid>
                  </Grid>
                )}
                {mostrarSeleccionCupones && (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        Seleccione uno o varios cupones de tarjeta en cartera:
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Buscar (marca, cupón, autorización, titular, monto...)"
                        value={busquedaCupon}
                        onChange={(e) => setBusquedaCupon(e.target.value)}
                        size="small"
                        sx={{ mb: 2 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                      {loadingCuponesCartera ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                          <Typography>Cargando cupones de tarjeta...</Typography>
                        </Box>
                      ) : cuponesCarteraDisponibles.length === 0 ? (
                        <Box sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            {cuponesCarteraConSesion.length === 0
                              ? 'No hay cupones disponibles en cartera.'
                              : 'Todos los cupones en cartera ya fueron agregados al listado de traspasos.'}
                          </Typography>
                        </Box>
                      ) : (() => {
                        const terminoBusqueda = busquedaCupon.toLowerCase().trim();
                        const cuponesFiltrados = terminoBusqueda
                          ? cuponesCarteraDisponibles.filter((cupon) => {
                              const coincideTexto =
                                cupon.nombreMarca?.toLowerCase().includes(terminoBusqueda) ||
                                String(cupon.nroComprobanteTa).includes(terminoBusqueda) ||
                                cupon.numeroroAutorizacion?.toLowerCase().includes(terminoBusqueda) ||
                                cupon.titularTarjeta?.toLowerCase().includes(terminoBusqueda) ||
                                cupon.nroTarjeta?.toLowerCase().includes(terminoBusqueda);
                              const montoBusqueda = parseFloat(terminoBusqueda.replace(/[^\d.,]/g, '').replace(',', '.'));
                              const coincideMonto =
                                !isNaN(montoBusqueda) && Math.abs((cupon.montoOperacion ?? 0) - montoBusqueda) < 0.01;
                              return coincideTexto || coincideMonto;
                            })
                          : cuponesCarteraDisponibles;
                        const cuponesOrdenados = ordenarCupones(cuponesFiltrados);
                        const todosSeleccionados =
                          cuponesOrdenados.length > 0 &&
                          cuponesOrdenados.every((c) => {
                            const nro = nroCargaCuponValido(c.nroCarga);
                            return nro != null && cuponesCarteraSeleccionados.has(nro);
                          });
                        return (
                          <>
                            <TableContainer component={Paper} sx={{ maxHeight: 400, overflowY: 'auto' }}>
                              <Table size="small" stickyHeader>
                                <TableHead>
                                  <TableRow>
                                    <TableCell padding="checkbox" sx={{ width: 50 }}>
                                      <Checkbox
                                        indeterminate={
                                          cuponesCarteraSeleccionados.size > 0 && !todosSeleccionados
                                        }
                                        checked={todosSeleccionados && cuponesOrdenados.length > 0}
                                        onChange={() => handleSeleccionarTodosCupones(cuponesOrdenados)}
                                      />
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>
                                      <TableSortLabel
                                        active={orderByCupon === 'nombreMarca'}
                                        direction={orderByCupon === 'nombreMarca' ? orderCupon : 'asc'}
                                        onClick={() => handleRequestSortCupon('nombreMarca')}
                                      >
                                        Marca
                                      </TableSortLabel>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>
                                      <TableSortLabel
                                        active={orderByCupon === 'nroComprobanteTa'}
                                        direction={orderByCupon === 'nroComprobanteTa' ? orderCupon : 'asc'}
                                        onClick={() => handleRequestSortCupon('nroComprobanteTa')}
                                      >
                                        N° Cupón
                                      </TableSortLabel>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>
                                      <TableSortLabel
                                        active={orderByCupon === 'numeroroAutorizacion'}
                                        direction={orderByCupon === 'numeroroAutorizacion' ? orderCupon : 'asc'}
                                        onClick={() => handleRequestSortCupon('numeroroAutorizacion')}
                                      >
                                        Autorización
                                      </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                      <TableSortLabel
                                        active={orderByCupon === 'montoOperacion'}
                                        direction={orderByCupon === 'montoOperacion' ? orderCupon : 'asc'}
                                        onClick={() => handleRequestSortCupon('montoOperacion')}
                                      >
                                        Monto
                                      </TableSortLabel>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>
                                      <TableSortLabel
                                        active={orderByCupon === 'titularTarjeta'}
                                        direction={orderByCupon === 'titularTarjeta' ? orderCupon : 'asc'}
                                        onClick={() => handleRequestSortCupon('titularTarjeta')}
                                      >
                                        Titular
                                      </TableSortLabel>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>
                                      <TableSortLabel
                                        active={orderByCupon === 'fecha'}
                                        direction={orderByCupon === 'fecha' ? orderCupon : 'asc'}
                                        onClick={() => handleRequestSortCupon('fecha')}
                                      >
                                        Fecha
                                      </TableSortLabel>
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {cuponesOrdenados.length === 0 ? (
                                    <TableRow>
                                      <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                                        <Typography variant="body2" color="text.secondary">
                                          No se encontraron cupones que coincidan con la búsqueda
                                        </Typography>
                                      </TableCell>
                                    </TableRow>
                                  ) : (
                                    cuponesOrdenados.map((cupon) => {
                                      const nroCarga = nroCargaCuponValido(cupon.nroCarga);
                                      if (nroCarga == null) return null;
                                      const estaSeleccionado = cuponesCarteraSeleccionados.has(nroCarga);
                                      return (
                                        <TableRow
                                          key={nroCarga}
                                          hover
                                          selected={estaSeleccionado}
                                          sx={{ cursor: 'pointer' }}
                                          onClick={() => {
                                            const nuevoSet = new Set(cuponesCarteraSeleccionados);
                                            if (estaSeleccionado) nuevoSet.delete(nroCarga);
                                            else nuevoSet.add(nroCarga);
                                            setCuponesCarteraSeleccionados(nuevoSet);
                                          }}
                                        >
                                          <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                            <Checkbox
                                              checked={estaSeleccionado}
                                              onChange={(e) => {
                                                const nuevoSet = new Set(cuponesCarteraSeleccionados);
                                                if (e.target.checked) nuevoSet.add(nroCarga);
                                                else nuevoSet.delete(nroCarga);
                                                setCuponesCarteraSeleccionados(nuevoSet);
                                              }}
                                            />
                                          </TableCell>
                                          <TableCell>{cupon.nombreMarca || cupon.codigoMarca}</TableCell>
                                          <TableCell>{cupon.nroComprobanteTa}</TableCell>
                                          <TableCell>{cupon.numeroroAutorizacion}</TableCell>
                                          <TableCell align="right" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                            {formatCurrency(cupon.montoOperacion ?? 0)}
                                          </TableCell>
                                          <TableCell>{cupon.titularTarjeta || '-'}</TableCell>
                                          <TableCell>
                                            {cupon.fecha ? new Date(cupon.fecha).toLocaleDateString() : '-'}
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })
                                  )}
                                </TableBody>
                              </Table>
                            </TableContainer>
                            {cuponesCarteraSeleccionados.size > 0 && (
                              <Typography variant="body2" color="primary" sx={{ mt: 1, fontWeight: 600 }}>
                                {cuponesCarteraSeleccionados.size} cupón
                                {cuponesCarteraSeleccionados.size > 1 ? 'es' : ''} seleccionado
                                {cuponesCarteraSeleccionados.size > 1 ? 's' : ''} · Total:{' '}
                                {formatCurrency(
                                  cuponesSeleccionadosLista.reduce((s, c) => s + (c.montoOperacion ?? 0), 0)
                                )}
                              </Typography>
                            )}
                          </>
                        );
                      })()}
                    </Grid>
                  </Grid>
                )}
                {(esTransferenciaBancaria || esOtros) && (
                  <Typography variant="body2" color="text.secondary">
                    Los datos del medio ya están cargados según la chequera de destino seleccionada. Complete detalle, observaciones y monto arriba.
                  </Typography>
                )}
              </Box>
            )}
          </Paper>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3, mb: 2, color: '#333' }}>
            Traspasos a realizar
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Origen</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Grupo destino</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Destino</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Detalle</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Observaciones</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Monto</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }} width={80}>Quitar</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transferenciasPendientes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }} color="text.secondary">
                      Agregue traspasos con el botón de arriba
                    </TableCell>
                  </TableRow>
                ) : (
                  transferenciasPendientes.map((item, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell>{item.mcpOrigen || '-'}</TableCell>
                      <TableCell>{item.grupoDestinoNombre || item.grupoDestino || '-'}</TableCell>
                      <TableCell>{item.mcpDestino || '-'}</TableCell>
                      <TableCell>{item.detalle || '-'}</TableCell>
                      <TableCell>{item.observaciones || '-'}</TableCell>
                      <TableCell align="right">{formatCurrency(item.importe)}</TableCell>
                      <TableCell align="center">
                        <IconButton size="small" color="error" title="Quitar" onClick={() => handleQuitarPendiente(idx)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="body1" fontWeight={600} sx={{ mb: 2 }}>
            Total: {formatCurrency(transferenciasPendientes.reduce((s, t) => s + t.importe, 0))}
          </Typography>
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button variant="outlined" startIcon={<CancelIcon />} onClick={handleCancelar} sx={{ textTransform: 'none' }}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<CheckIcon />}
              onClick={handleGuardarTodas}
              disabled={transferenciasPendientes.length === 0 || guardandoTodas}
              sx={{ textTransform: 'none' }}
            >
              {guardandoTodas ? 'Guardando...' : 'Guardar todas'}
            </Button>
          </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ position: { md: 'sticky' }, top: { md: 16 } }}>
                <Stack spacing={2}>
                  <Box
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 2,
                      p: 2,
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      textTransform="uppercase"
                      letterSpacing={0.5}
                      display="block"
                      mb={1.5}
                    >
                      Datos del traspaso
                    </Typography>
                    <Stack spacing={1.5}>
                      <Autocomplete
                        {...searchableSelectAutocompleteProps}
                        options={grupos}
                        getOptionLabel={(option) => option.nombre || `Grupo ${option.codigo}`}
                        value={grupoSeleccionado}
                        onChange={(_, newValue) => {
                          if (hayTraspasosPendientes) return;
                          setGrupoSeleccionado(newValue);
                          setGrupoDestinoSeleccionado(null);
                          if (newValue) persistGrupoCajaChica(newValue);
                        }}
                        disabled={hayTraspasosPendientes}
                        loading={loadingGrupos}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            label="Grupo de Caja origen"
                            placeholder="Seleccionar grupo..."
                            helperText={
                              hayTraspasosPendientes
                                ? 'No se puede cambiar con traspasos en el listado'
                                : undefined
                            }
                          />
                        )}
                      />
                      <Autocomplete
                        {...searchableSelectAutocompleteProps}
                        options={gruposDestinoOpciones}
                        getOptionLabel={(option) => option.nombre || `Grupo ${option.codigo}`}
                        isOptionEqualToValue={(opt, val) => opt.codigo === val.codigo}
                        value={grupoDestinoSeleccionado}
                        onChange={(_, newValue) => {
                          if (grupoDestinoBloqueado || hayTraspasosPendientes) return;
                          setGrupoDestinoSeleccionado(newValue);
                          setChequeraDestino(null);
                        }}
                        disabled={!grupoSeleccionado || grupoDestinoBloqueado || hayTraspasosPendientes}
                        loading={loadingGruposDestino}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            label="Grupo de Caja destino"
                            placeholder={
                              grupoDestinoBloqueado
                                ? 'Definido por configuración'
                                : 'Seleccionar grupo destino...'
                            }
                            helperText={
                              hayTraspasosPendientes
                                ? 'No se puede cambiar con traspasos en el listado'
                                : grupoDestinoBloqueado
                                  ? 'Definido por la configuración del grupo origen'
                                  : undefined
                            }
                          />
                        )}
                      />
                    </Stack>
                  </Box>
                  <Box
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 2,
                      p: 2,
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      textTransform="uppercase"
                      letterSpacing={0.5}
                      display="block"
                      mb={1.5}
                    >
                      Datos de emisión
                    </Typography>
                    <Stack spacing={1.5}>
                    <SearchableSelect
                      label="Sucursal"
                      size="small"
                      required
                      loading={loadingSucursales}
                      options={sucursales.map((sucursal) => ({
                        value: sucursal.nroSuc,
                        label: sucursal.nombreSucursal?.trim()
                          ? `${sucursal.nroSuc} — ${sucursal.nombreSucursal.trim()}`
                          : `Suc. ${sucursal.nroSuc}`,
                      }))}
                      value={nroSucSeleccionado > 0 ? nroSucSeleccionado : null}
                      onChange={(value) => onSucursalChange(Number(value) || 0)}
                    />
                    <TextField
                      size="small"
                      fullWidth
                      label="Fecha de Emisión"
                      type="date"
                      value={fechaEmisionForm}
                      onChange={(e) => setFechaEmisionForm(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      required
                    />
                    <TextField
                      size="small"
                      fullWidth
                      label="Observaciones"
                      value={observaciones}
                      onChange={(e) => setObservaciones(e.target.value)}
                      multiline
                      minRows={2}
                      placeholder="Descripción u observaciones del traspaso"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                            <NotesIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                    </Stack>
                  </Box>
                </Stack>
              </Box>
            </Grid>
          </Grid>
          </>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={openGrupoCajaDialog}
        onClose={handleCancelarGrupoCaja}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
        fullScreen={isSmallScreen}
      >
        <DialogTitle>Seleccionar Grupo de Caja</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Elija el grupo de caja de origen para realizar el traspaso.
          </Typography>
          <Box sx={{ maxHeight: { xs: '60vh', sm: 360 }, overflowY: 'auto', pr: 1 }}>
            <Grid container spacing={2}>
              {grupos.map((g) => (
                <Grid item xs={12} key={g.id ?? g.codigo}>
                  <Card
                    variant={grupoPendienteId === g.id || grupoPendienteId === g.codigo ? 'elevation' : 'outlined'}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setGrupoPendienteId(g.id ?? g.codigo)}
                  >
                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}>
                      <input
                        type="radio"
                        checked={grupoPendienteId === g.id || grupoPendienteId === g.codigo}
                        onChange={() => setGrupoPendienteId(g.id ?? g.codigo)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ marginRight: 8 }}
                      />
                      <Box>
                        <Typography sx={{ fontWeight: 600 }}>{g.nombre}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Código: {g.codigo}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
              {grupos.length === 0 && !loadingGrupos && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    No hay grupos de caja disponibles.
                  </Typography>
                </Grid>
              )}
              {loadingGrupos && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={28} />
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelarGrupoCaja} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmarGrupoCaja}
            variant="contained"
            disabled={grupoPendienteId == null}
            sx={{ textTransform: 'none' }}
          >
            Seleccionar
          </Button>
        </DialogActions>
      </Dialog>

      {pdfUrl && selectedTransferenciaId != null && (
        <PDFViewer
          pdfUrl={pdfUrl}
          onClose={handleClosePDF}
          title={`Traspaso ${selectedTransferenciaId}`}
          documentId={`${selectedTransferenciaId}`}
          documentType="transferenciaCaja"
          downloadFileName={`traspaso-caja_${selectedTransferenciaId}.pdf`}
        />
      )}

      <ExportProgressDialog
        open={loadingChequesCartera && cargaChequesProgress.totalPages > 1}
        progress={cargaChequesProgress}
      />
    </Box>
  );
};

export default TesoreriaTransferencias;
